import { Fragment, Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import {
  line, fill, RECORD_ICONS, GEOLOCATION_REFUSAL_KEYS,
  Color, CREDIT_STATUSES, MAIL_ROUTE, EDIT_FIELDS, FIGURE_BLOCK_HEIGHT_RATIO, LIST_PAGES, NEW_DOCUMENT_ID, Size, TILE_ACTION_LABELS, asItemSupplierStatus, bannerPills, pageKindLine, t,
  type BannerAvatar, type CompanySuccession, type BannerFact, type BannerPill, type NoteDraft, type PagedRecord, type ProgressStageRow, type StatusResolver, type TileActionKind, type TilePlacement,
  type DealValueSource, type PrintSheetHtml, type PrintSheetLayout, type ChangeRequestView, type RecordSuggestionRow, CHANGE_REQUESTS_TILE,
  RECORD_PAGE_SPECS, associatedPersonFor, newQuoteQuery, type RecordActionKey, type RecordModalKey, type RecordPanelKey,
} from '@bms2/shared';
import { Button, Pill } from '../../components/controls';
import { Icon } from '../../components/Icon';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Tile, type TileItem } from '../../components/composites/Tile';
import { NoteComposer } from '../../components/composites/NoteComposer';
import { TileCanvas, type CanvasTile } from '../../components/composites/TileCanvas';
import { AccessDenied } from '../../shell/Fallbacks';
import { PageModule } from '../../shell/PageModule';
import { useSettingsGear } from '../settings/SettingsGear';
import { useDateFormat } from '../../shell/ViewerZone';
import type { ShellOutletContext } from '../../shell/AppShell';
import { PAGES_PATH, PICKER_CAP, apiFetch, del, patch, post, useApi, useRows, DEALS_PATH, FILES_PATH } from './api';
import { useNoteReplies } from './noteReplies';
import { ShareFileModal, type ShareDraft, type ShareRoute } from '../../components/files/ShareFileModal';
import { ScanConfirmModal } from './ScanConfirmModal';
import { FoldSiteModal, type SiteFold } from './FoldSiteModal';
import { ChangeOwnerModal, DealContactModal, DealTeamModal, LostDealModal, ReleaseFileModal, type TeamMemberEdit } from './DealModals';
import { CloneDealModal } from './DealCloneModal';
import { DealDeleteModal } from './DealDeleteModal';
import { copyDealPack, downloadDealPack, type DealPackOutcome } from './DealPack';
import { type BomEditorLine } from './DealBomEditor';
import { type BomListRow } from './DealBomList';
import { BannerPills } from './BannerPills';
import { SuccessionSentence } from './SuccessionSentence';
import { DealBannerColumn } from './DealBannerColumn';
import { DealNextActionBanner } from './DealNextActionBanner';
import { DealFloorWorking } from './DealFloorWorking';
import { DealNameOwner } from './DealNameOwner';
import { DealRequestBand, type ServiceRequestBandView } from './DealRequestBand';
import { DealQuoteState, type PartsQuoteView } from './DealQuoteState';
import { DealRequestStatus, type RequestStatusView } from './DealRequestStatus';
import { DealProgress, ProgressRail } from './DealProgress';
import { DealStageCard, DealStageReadCard, type LatestQuoteView, type RfqCardRow, type StageCardAnswer } from './DealStageCard';
import type { SectionNoteReplies } from './DealSectionNotes';
import { withAuthorityTags, type AuthorityRowView } from './DealAuthority';
import type { DealBudgetView } from './DealBudget';
import type { DealTimingView } from './DealTiming';
import { LeadProgress, LeadRail, LeadStageCards, LEADS_PATH, type LeadCardAnswer } from './LeadStageCards';
import { StagedLayout } from './StagedLayout';
import { PROGRESS_PANEL_KEY_PREFIX } from '../../shell/devicePreference';
import { STATEMENTS_ROUTE } from '../accounting/api';
import { AccountRoleModal, ConvertToDealModal, DispatchModal, NotAFitModal, RedirectModal } from './LeadModals';
import { RfqModal } from './RfqModal';
import { VendorRepModal } from './VendorRepModal';
import { AccountStandingModal, type AccountStandingEdit } from './AccountStandingModal';
import { ContactMethodModal, type ContactMethodEdit, type ContactMethodJob } from './ContactMethodModal';
import { CustomFieldsModal } from './CustomFieldsModal';
import { EditRecordModal } from './EditRecordModal';
import { newDealRoute, type NewDealArrival } from './NewDealPage';
import { ItemAttributeModal, ItemImageBlock, ItemImageModal, ItemSiteStockModal, ItemSupplierModal, type SupplierEdit } from './ItemModals';
import { ItemTransactionsPanel } from './ItemTransactionsPanel';
import { LookUpManufacturerButton } from './LookUpManufacturer';
import { PersonTextsPanel } from './PersonTextsPanel';
// Decision 0616: what the banner's avatar and aside slots hold, and the photo upload behind the camera badge.
import { BannerAvatarSlot } from './RecordBannerHead';
import { RecordPictureModal } from './RecordPictureModal';
import { EmploymentModal, type EmploymentEdit } from './EmploymentModal';
import { SetLevelModal, type SetLevelEdit } from './SetLevelModal';
import { DeleteLinkModal } from './DeleteLinkModal';
import { SiteOccupancyModal, type SiteOccupancyEdit } from './SiteOccupancyModal';
import { RecordEventModal } from './RecordEventModal';
import { OfficeCallButton } from './OfficeCall';
import { RecordCheckAct, SuggestionsPanel } from './RecordSuggestions';
import { EducationModal, PersonEducationPanel, PersonOtherSkillsPanel, PersonProfilePanel, announcePersonHistory } from './PersonHistoryPanels';
import { GrantRoleWindow, PersonConfidentialityPanel, announceConfidentiality } from './PersonConfidentialityPanel';
import { EventBookingList } from '../../components/composites/EventBooking';
import { toSummary, type CalendarEventRow } from './CalendarPage';
import { RecordNoteModal, addNoteText } from './RecordNoteModal';
import { lazyGroup } from '../../shell/lazyGroup';
import { LocationVerifyPanel, placedPrefill, useLocationVerify } from './LocationVerify';
import { LocationPinPanel } from './LocationPin';
import { readPosition } from './geolocation';
import { RecordUploadModal } from './RecordUploadModal';
import { SiteModal } from './SiteModal';
import { CertificateRequestsPanel } from './CertificateRequests';
import { ExemptCustomerStepsWindow } from './ExemptCustomerSteps';
import { SiteTaxJurisdictionPanel } from './SiteTaxJurisdiction';
import { PlantFeedPanel } from './PlantFeedPanel';
import { CompanyTermsSetModal } from './CompanyTermsSetModal';
import { CertificateEndModal, CertificateModal, CertificateMoveModal, CertificateRemoveModal, SiteRequirementEndModal, SiteRequirementModal, SiteRoleModal, type CertificateEdit } from './SiteModals';
import { SkillModal } from './SkillModal';
import { FileDetailsModal, type FileDetailsRow } from './FileDetailsModal';
import { TagModal } from './TagModal';
import { ChangeRequestsPanel, RequestChangeButton, acceptRequestOf } from './ChangeRequests';
import { JobMessageRules } from './JobMessageRules';
import { IssueStockModal } from './StockDocumentPages';
import { RecordTagNotes, RecordTagPills, RecordTagStrip, TagNoteModal } from './RecordTags';
import { NDA_PATH, NdaModal } from './NdaModal';
import { DocumentViewPage } from './DocumentViewPage';
import { DocumentFilesSection } from './DocumentFilesSection';
import { DocumentHistoryPanel } from './DocumentHistoryPanel';
import { documentPdfPath } from './ViewPdfLink';
import { WorkOrderProgress } from './WorkOrderProgress';
import { RefusalText } from './RefusalText';
import { WorkOrderFooter, WorkOrderLines, WorkOrderMaterials, WorkOrderOptions, WorkOrderTravel, type WorkOrderView } from './WorkOrderPanels';
import { WorkOrderVisits } from './WorkOrderVisits';
import { WorkOrderDocuments } from './WorkOrderDocuments';
import { WorkOrderLabour } from './WorkOrderLabour';
import { DealTeamPanel } from './DealTeamPanel';
import type { DrawnTag, FrozenDealTeam, LinkTarget, NextActionBannerRow, UploadAct, WorkOrderProgressView } from '@bms2/shared';
import { HIRE_WINDOW_ROUTE, RECORD_TILE_PARAM, drawsGroupedStrip, recordHref, isCompanyMailboxKind, isDocumentAttachmentKind, positionListOf, type DealMeetingView, type DiscoveryCardAnswer } from '@bms2/shared';
import { itemsFor, noteDraftFrom, type TileAnswer, type TileRow } from './tileItems';
import { releaseCompanies } from './releaseFiles';
import { certificateMenuActs, rowActionsFor, type EndableTile } from './rowActions';
import { CompanyMailboxModal, type CompanyMailboxEdit } from './CompanyMailboxModal';
import { CompanyOwnershipModal, ownershipEndingOf, type OwnershipWindow } from './CompanyOwnershipModal';
import { RaiseComplaintAction } from '../hr/RaiseComplaintAction';
import styles from './RecordPage.module.css';
import { reportCaughtInBrowser } from '../../shell/systemErrors';
import { useSuperAdmin } from '../../shell/SuperAdmin';
import { InspectWindow } from './InspectWindow';

// 🔨 The vendor's tax panel loads when a vendor's page first draws it, never with every record page (piece 12; decision 10641).
const VendorTaxPanel = lazyGroup(() => import('../banking/VendorTaxPanel'))('VendorTaxPanel');
// 🔒 Decision 12540: the Site page's Assets tile draws the asset register's own tree, loaded with the register's code.
const SiteAssetsPanel = lazyGroup(() => import('../assets/SiteAssetsPanel'))('SiteAssetsPanel');

/**
 * The Record Page archetype (piece 13): every content section is a locked Tile
 * on the Tile Canvas, in the order the plan fixes, each drawing its rows
 * through the item type the register names and carrying the action the source
 * figure gives it — + Add Note, + Add Contact, ↑ Upload, + Add Event.
 * 🔨 A tile a later piece owns is drawn with the note that names the piece
 * rather than being left out silently, so the page reads as complete-but-pending
 * instead of missing. 🔒 A Person page carries no HR tile: those are sections
 * of the Employee page in HR (decision 3180).
 */
/* 💰 The floor was 240, which no record tile can actually use: the canvas takes its
   third column as soon as the arithmetic allows, so a 900px canvas gave three columns of
   292px and a 1280px window with the nav open gave 321px. Measured in a browser on
   2026-09-13, a Requirements row with its pill wraps to a second line at 321px and sits on
   one at 349px, with a label shorter than a real one. Nothing truncated, so neither the
   overlap detector nor a truncation check could see it — the rows just grew taller and read
   as cramped (product owner, 2026-09-11: "jammed to the left ... notes is way too tight").
   360px is that measured break with headroom, and it is the guard in `columnsFor` that drops
   a column rather than a separate rule. */
export const RECORD_TILE_MIN_WIDTH = 360;
/* 💰 The cap was 1200, so a full-width Tile on a three-column canvas stopped short of the third column on a wide screen and the page looked unfilled (product owner, 2026-09-11: "width is off here too"). The widest breakpoint is the cap now. */
export const RECORD_TILE_MAX_WIDTH = 1920;

export const RECORD_CANVAS_OPTIONS = { columns: 3, twoColumnBelow: 900, oneColumnBelow: 560, gap: 12, blockHeightRatio: FIGURE_BLOCK_HEIGHT_RATIO };

interface PageAnswer {
  /** 🔒 A merged Item's old id answers the item that stays and nothing else, and the page opens it (decision 7470). */
  movedTo?: string;
  recordType: PagedRecord;
  recordId: string;
  title: string;
  tiles: TileAnswer[];
  facts: Record<string, string | boolean | null>;
  /** The record's own editable values where the tiles' labels cannot carry them (an Item's ids and prices, piece 17). */
  editable?: Record<string, unknown>;
  /** What the banner's picture slot draws, and whether this reader may change it (decision 0616; T170). */
  bannerAvatar?: BannerAvatar | null;
  /** 🔒 Each Deal contact's own tags, keyed by person, drawn after the Authority pill (decision 2262). */
  contactTags?: Record<string, DrawnTag[]>;
  /** The Deal page's own constructions (piece 19): the ladder and the current stage's card. */
  progress?: ProgressStageRow[];
  stageCard?: StageCardAnswer | null;
  stageCards?: StageCardAnswer[];
  /** 🔒 The kinds an existing-equipment photo takes, which a Deal's Upload window offers (decision 5440). */
  equipmentPhotoKinds?: string[];
  /** The Deal's RFQs as the RFQ Issued expander lists them (decision 0332). */
  rfqs?: RfqCardRow[];
  /** The Lead page's own construction (piece 20): the three StageCards. */
  cards?: LeadCardAnswer[];
  /** Every line of every bill of materials the Deal carries, which the BOM Built card reads (decision 0332). */
  bom?: BomEditorLine[];
  /** The Deal's bills of materials, the rows its tile draws (decision 0565). */
  bomLists?: BomListRow[];
  /** 🔒 Who on the buying side carries weight, drawn under the Authority row (decision 0618). */
  authority?: AuthorityRowView[];
  /** 🔒 The Intent card's Budget row: the Target, the Maximum and every pair saved (decision 0664). */
  budget?: DealBudgetView | null;
  /** 🔒 The Timing row's three dates (decision 0719). */
  timing?: DealTimingView | null;
  /** 🔒 The newest Quote, which the Negotiation card's Submit Quote row reads inline (decision 0666). */
  latestQuote?: LatestQuoteView | null;
  /** The Work Order page's own constructions (piece 22): the lines with their workers, the parts and the travel line. */
  workOrder?: WorkOrderView;
  /** 🔒 Job Progress, built on the server beside the Work Order's own read (decision 6020). */
  workOrderProgress?: WorkOrderProgressView;
  /** 🔒 The Deal's team as it stood when the Work Order was created (decision 0765). */
  dealTeam?: FrozenDealTeam | null;
  /** 🔒 Whether this viewer may approve the Work Order now, the queue's own answer for this one document (T644; decision 1521). */
  canApprove?: boolean;
  /** 🔒 Why this viewer writes nothing on the document, or null where they may (decision 0687); a record that is not a document answers none. */
  writeRefusal?: string | null;
  /** 🔒 The layout the document prints on, so view mode positions the regions the sheet positions (decision 0567). */
  printLayout?: PrintSheetLayout | null;
  /** 🔒 The sheet the PDF view draws, as the printer's own HTML, in this same answer (T169). */
  printSheet?: PrintSheetHtml | null;
  /** 🔒 The classification pills that stand in the kind line's place on a Company and a Person (T175); the server computes them. */
  classifications?: readonly BannerPill[] | null;
  /** 🔒 A Company's banner sentences beside the status pill: who bought it, and the companies it bought (decision 7690). */
  succession?: CompanySuccession | null;
  /** 🔒 The record the kind line names after its type, as a link: a Deal's customer (decision 0722). */
  kindParent?: { text: string; route: string | null } | null;
  /** 🔒 The request band's facts on a Deal carrying a request row (decision 1912). */
  serviceRequest?: ServiceRequestBandView | null;
  /** 🔒 Where the job stands on a request, derived and never stored (decision 1913). */
  requestStatus?: RequestStatusView | null;
  /** 🔒 Name the Owner on a request waiting for one, or on an older emergency (decisions 1914, 1992). */
  nameOwner?: { ownerName: string } | null;
  /** 🔒 The Parts page's Quote state while its Deal is open (decision 2390). */
  partsQuote?: PartsQuoteView | null;
  /** 🔒 The Discovery card's one shape (decision 5450), drawn as the Discovery stage card's body. */
  discovery?: DiscoveryCardAnswer | null;
  /** 🔒 What Set Meeting and Mark Intent Complete read (decision 6001); absent from an older reply. */
  meeting?: DealMeetingView | null;
}

export interface RecordPageProps {
  recordType: PagedRecord;
  recordId: string;
  apiBaseUrl?: string;
  /** The viewer, from the frame; the route element reads the shell's context and hands them in. */
  personId?: string;
  employmentId?: string | null;
  timeZone?: string;
}

/** 🔒 Decision is a card like the other four since decision 0595; it is where a Deal is won or lost. */
/** 🔒 The acts a record's banner row draws before the rest fold under More (decision 6685). */
const MAX_BANNER_ACTS = 4;

const GATED_STAGES = ['Intent', 'Discovery', 'Evaluation', 'Negotiation', 'Decision', 'Won'];

function flowOf(tiles: readonly CanvasTile[], keep: (key: string) => boolean) {
  return tiles.filter((tile) => keep(tile.placement.id)).map((tile) => ({ key: tile.placement.id, span: tile.placement.span, node: tile.render() }));
}

/**
 * The Deal's stage cards (figure 82): every gated stage the Deal has entered
 * editable, the current one with Back and Mark Complete, a stage not yet
 * reached read off the ladder's rows, Lost as a note (decision 0331).
 *
 * 🔒 Decision is one of the cards since decision 0595, carrying Won, Mark Lost
 * and its own clock; it drew as a one-line hint with its buttons in the banner,
 * which put the end of the deal somewhere other than the stage that ends it.
 */
function dealStages(
  answer: PageAnswer,
  facts: PageAnswer['facts'],
  apiBaseUrl: string,
  saved: () => void,
  onError: (message: string) => void,
  onNotice: (message: string) => void,
  hooks: { onRfq: () => void; onWon: () => void; onLost: () => void; onDraftQuote: () => void },
  notes: { personId: string; timeZone: string; replies: SectionNoteReplies },
  writes = true,
  shown: { stages: ReadonlySet<string>; toggle: (stage: string, open: boolean) => void } | null = null,
) {
  /*
   * 🔒 The page draws the cards its type and route name (decision 2293): the answer's ladder carries those
   * stages alone, so a request draws its band and its Won card, and a Quick Parts deal its Evaluation card.
   * 🔨 An answer from before the page by type still draws a request's Won card alone (decision 1912).
   */
  const rows = (answer.progress ?? []).filter((row) => GATED_STAGES.includes(row.stage) && (facts.isRequest !== true || row.stage === 'Won'));
  const cards = answer.stageCards ?? (answer.stageCard ? [answer.stageCard] : []);
  // The Decision card's clock: its days at Decision, counted from the stage gate's own enteredAt, with the STALE text where it is past threshold.
  const decisionDays = Number(facts.daysInStage ?? 0);
  const decisionNote = line(
    t('deal.card.decision'),
    t('deal.card.decisionClock'),
    `${decisionDays === 1 ? t('deal.card.oneDay') : `${decisionDays} ${t('deal.card.days')}`}.`,
    typeof facts.staleText === 'string' ? facts.staleText : '',
  );
  return (
    <>
      {facts.isLost === true ? (
        <p className={styles.hint} data-stage-card-note={String(facts.stage ?? '')}>{t('deal.card.lostNote')}</p>
      ) : null}
      {/* 🔒 Name the Owner first, where the invoice from the job waits on it (decisions 1914, 1992). */}
      {answer.nameOwner ? <DealNameOwner dealId={answer.recordId} ownerName={answer.nameOwner.ownerName} writes={writes} apiBaseUrl={apiBaseUrl} onSaved={saved} onError={onError} /> : null}
      {/* 🔒 The request band, beside the Deal's existing tiles, on a Deal carrying a request row (decision 1912). */}
      {answer.serviceRequest ? <DealRequestBand dealId={answer.recordId} band={answer.serviceRequest} writes={writes && facts.isLost !== true} apiBaseUrl={apiBaseUrl} onError={onError} onSaved={saved} /> : null}
      {rows.map((row) => {
        const card = cards.find((c) => c.stage === row.stage);
        /**
         * 🔨 Decision and Won are not drawn until the Deal reaches them (decision
         * 1216): the ladder at the right already names them, and an empty card
         * below the current one pushed the documents down the page. A Lost Deal's
         * cards still all read (decision 0967).
         */
        if ((row.stage === 'Decision' || row.stage === 'Won') && facts.isLost !== true && !(card?.entered ?? row.state !== 'todo')) return null;
        const editable = card ? (card.editable ?? card.current ?? card.stage === facts.stage) : false;
        const atDecision = row.stage === 'Decision' && facts.atDecision === true;
        /**
         * 🔒 A card carrying a list draws it whether or not the stage has been
         * entered (decision 0595). The bills of materials are built before
         * Evaluation is reached and read long after it, and a Quick Deal opens
         * at Won with only that gate; a read card would leave both with no way
         * to a list the Deal carries. Its rows still take no tick.
         */
        const carriesList = card?.rows.some((r) => r.listKind) === true;
        // 🔒 Discovery is not locked behind Intent (decision 5461): its card draws, and takes its writes, before the stage is entered.
        const carriesDiscovery = row.stage === 'Discovery' && (answer.discovery ?? null) !== null;
        return card && (editable || carriesList || carriesDiscovery)
          ? (
            <DealStageCard
              key={row.stage}
              dealId={answer.recordId}
              card={{ ...card, current: card.current ?? card.stage === facts.stage }}
              bomLists={answer.bomLists ?? []}
              authority={answer.authority ?? []}
              budget={answer.budget ?? null} timing={answer.timing ?? null}
              latestQuote={answer.latestQuote ?? null}
              onDraftQuote={hooks.onDraftQuote}
              rfqs={answer.rfqs ?? []}
              seesCost={facts.seesCost === true}
              listsEditable={facts.isLost !== true}
              bomLocked={facts.isDone === true && facts.isLost !== true ? 'done' : null}
              writes={writes}
              apiBaseUrl={apiBaseUrl}
              onChanged={saved}
              onError={onError}
              onNotice={onNotice}
              onRfq={hooks.onRfq}
              complete={row.state === 'done'}
              daysAtStage={card.stage === facts.stage ? Number(facts.daysInStage ?? 0) : null}
              {...(shown ? { shown: shown.stages.has(row.stage), onShown: (open: boolean) => shown.toggle(row.stage, open) } : {})}
              personId={notes.personId}
              timeZone={notes.timeZone}
              sectionReplies={notes.replies}
              discovery={answer.discovery ?? null}
              meeting={row.stage === 'Intent' ? answer.meeting ?? null : null}
              {...(atDecision ? { note: decisionNote } : {})}
              {...(atDecision
                ? {
                    actions: (
                      <>
                        <Button size={Size.Medium} color={Color.Navy} text={[t('deal.action.won')]} actions={[hooks.onWon]} />
                        <Button size={Size.Medium} color={Color.Inactive} text={[t('deal.action.lost')]} actions={[hooks.onLost]} />
                      </>
                    ),
                  }
                : {})}
            />
          )
          : <DealStageReadCard key={row.stage} row={row} dealId={answer.recordId} authority={facts.isLost === true ? answer.authority ?? [] : null} />;
      })}
      {/* 🔒 The Parts page's Quote state, under its Evaluation card where the Won card stands once the Quote wins the Deal (decision 2390). */}
      {answer.partsQuote ? <DealQuoteState view={answer.partsQuote} onDraftQuote={writes && facts.isLost !== true ? hooks.onDraftQuote : null} /> : null}
    </>
  );
}

/** The Flags tile's pills: a raised flag is a warning, a tag is a plain label; neither gates anything (decision 0226). */
const flagResolver: StatusResolver = (entityType, statusValue) => ({ label: statusValue, colorGroup: entityType === 'RecordFlag' ? 'warning' : 'inactive' });

type OpenModal = TileActionKind | 'logContact' | 'editNote' | 'setImage' | 'setPhoto' | 'fileDetails' | 'editEmployment' | 'editOccupancy' | 'issueStock' | 'changeOwner' | null;

/** 🔒 The information the Deal value came from, as the server names it (decision 0726); anything else reads as no source. */
function dealValueSourceOf(value: string | boolean | null | undefined): DealValueSource | null {
  return value === 'quote' || value === 'bom' || value === 'budget' || value === 'entered' ? value : null;
}

/** A page fact that is text, or nothing. */
function stringFact(value: string | boolean | null | undefined): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function RecordPage({ recordType, recordId, apiBaseUrl = '', personId = '', employmentId = null, timeZone = 'UTC' }: RecordPageProps) {
  const dateFormat = useDateFormat();
  const navigate = useNavigate();
  const [modal, setModal] = useState<OpenModal>(null);
  const [fileDetails, setFileDetails] = useState<FileDetailsRow | null>(null);
  /** The Attributes row being edited; null opens the modal to add one (piece 17, Tile 5). */
  const [editingAttribute, setEditingAttribute] = useState<string | null>(null);
  const [notice, setNotice] = useState<ReactNode>(null);
  /** 🔒 A refusal that carries a route draws its link after the sentence (T3511); the Work Order panels pass the body. */
  const refuse = (message: string, body?: unknown) => setNotice(<RefusalText message={message} body={body} />);
  /** Files dropped on the Files tile, handed to the upload modal as it opens. */
  const [dropped, setDropped] = useState<readonly File[]>([]);
  /** 🔒 Decision 9680: the act that opened the upload, Employment Documents on the Employments tile; a Files tile names none. */
  const [uploadAct, setUploadAct] = useState<UploadAct | null>(null);
  /** The Deal page (piece 19): the History tile pages by month, the Lost dialog, and a file row's Release. */
  const [historyMonths, setHistoryMonths] = useState(1);
  const [lostOpen, setLostOpen] = useState(false);
  /** 🔒 The Clone window (decision 1482). */
  const [cloneOpen, setCloneOpen] = useState(false);
  // 🔒 Decision 12700: the Delete Deal window, keyed to its opening so each opening reads what the Deal holds afresh.
  const [deleteOpen, setDeleteOpen] = useState(false);
  // 🔒 Decision 7620: Inspect, a super admin's read of what points at the record, from the banner menu.
  const superAdmin = useSuperAdmin();
  const [inspecting, setInspecting] = useState(false);
  // 🔨 Inspect is mounted only while More is open: a button inside a closed menu still measures in Chromium, and a walk that counts it cannot press it.
  const [moreOpen, setMoreOpen] = useState(false);
  const [release, setRelease] = useState<{ fileId: string; fileName: string } | null>(null);
  /** Share on a file row (decision 0345): the one Share modal on that file, on every Files tile. */
  const [share, setShare] = useState<{ fileId: string; fileName: string } | null>(null);
  /** A held photo upload the pipeline waits on: the review screen opens on it at once (decision 0345). */
  const [scanConfirm, setScanConfirm] = useState<{ id: string; filename: string } | null>(null);
  /** 🔒 The Share modal's recipient picker asks the people list with the typed words, as every person picker does (T539). */
  const searchSharePeople = useCallback(async (query: string, signal: AbortSignal) => {
    const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/PersonRecord?${PICKER_CAP}&q=${encodeURIComponent(query)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: { id: string; fullName: string; workEmail: string | null }[] };
    return body.rows.map((person) => ({ id: person.id, name: person.fullName, email: person.workEmail }));
  }, [apiBaseUrl]);
  const shareDetail = useApi<{ file: { confidentialityRoleId: string | null } }>(share ? `${FILES_PATH}/${share.fileId}` : null, apiBaseUrl);
  /** The Item's supplier row being edited; null adds one (decision 0296). */
  const [editingSupplier, setEditingSupplier] = useState<SupplierEdit | null>(null);
  const [editingContact, setEditingContact] = useState<ContactMethodEdit | null>(null);
  /** 🔒 The company mailbox row whose pencil was pressed; null adds one (decision 2972). */
  const [editingMailbox, setEditingMailbox] = useState<CompanyMailboxEdit | null>(null);
  /** The employment row whose pencil was pressed: the title held today, the job's work email and work phone, and its own dates (piece 13; decision 0589). */
  const [editingEmployment, setEditingEmployment] = useState<EmploymentEdit | null>(null);
  /** 🔒 The open position at our own Company whose Set level pencil was pressed (T726; decision 1495). */
  const [settingLevel, setSettingLevel] = useState<SetLevelEdit | null>(null);
  /** 🔒 The link whose Delete this link glyph was pressed (T728; decision 1500). */
  const [deletingLink, setDeletingLink] = useState<LinkTarget | null>(null);
  /** 🔒 The site row whose Fold into was pressed, and the place tiles whose Former sites are open (decision 1920). */
  const [foldingSite, setFoldingSite] = useState<SiteFold | null>(null);
  const [openFormer, setOpenFormer] = useState<ReadonlySet<string>>(new Set());
  /** The site row whose pencil was pressed, on the Company's Sites tile or the Location's Occupants tile (decision 0589). */
  const [editingOccupancy, setEditingOccupancy] = useState<SiteOccupancyEdit | null>(null);
  const [editingMember, setEditingMember] = useState<TeamMemberEdit | null>(null);
  /** The viewer's own note being edited: its id and what it holds (decision 0301). */
  const [editingNote, setEditingNote] = useState<{ id: string; draft: NoteDraft } | null>(null);
  /** The Site's requirement row being ended (decision 0295). */
  const [endingRequirement, setEndingRequirement] = useState<{ id: string; label: string; value: string; removable: boolean } | null>(null);
  /** Decision 0676: the certificate row Edit opens the modal on, and the one End asks about. */
  const [editingCertificate, setEditingCertificate] = useState<CertificateEdit | null>(null);
  const [endingCertificate, setEndingCertificate] = useState<{ id: string; label: string; value: string } | null>(null);
  /** 🔒 Decision 7891: the certificate a Remove or a Move asks about, from its card or from the Exempt Customer Steps. */
  const [removingCertificate, setRemovingCertificate] = useState<{ id: string; label: string; value: string } | null>(null);
  const [movingCertificate, setMovingCertificate] = useState<{ id: string; label: string; value: string } | null>(null);
  /** 🔒 Decision 2851: the Exempt Customer Steps window, which stays open beneath the certificate window it opens. */
  const [stepsOpen, setStepsOpen] = useState(false);
  /** Decision 0676: a certificate row opens the site's one certificate window on its own facts; Exempt customer's steps open the same (decision 1781). */
  const editCertificateRow = (row: TileRow) => {
    const raw = row.raw ?? {};
    const text = (key: string) => (typeof raw[key] === 'string' ? (raw[key] as string) : null);
    setEditingCertificate({ id: row.id ?? '', stateCode: text('stateCode') ?? '', exemptionReason: text('exemptionReason') ?? '', municipality: text('municipality'), formType: text('formType'), effectiveFrom: text('effectiveFrom') ?? '', expiresOn: text('expiresOn'), fileId: text('fileId') ?? '', fileName: text('fileName'), awaitingReview: raw.awaitingReview === true });
    setModal('addCertificate');
  };
  /** 🔒 The client's terms set being chosen from the Access & agreements tile (decision 1172); null while the window is shut. */
  const [choosingTermsSet, setChoosingTermsSet] = useState<{ setId: string | null } | null>(null);
  /** 🔒 Decision 7501: + Add Owner or End Ownership on the Company page, mounted only while open. */
  const [ownershipWindow, setOwnershipWindow] = useState<OwnershipWindow | null>(null);
  /** The Lead page's own dialogs (piece 20): Not a Fit, Convert to Deal, Redirect and Dispatch; the Company's + Add Role. */
  const [leadModal, setLeadModal] = useState<'notFit' | 'convertDeal' | 'redirect' | 'dispatch' | 'accountRole' | null>(null);
  /** 🔒 Decision 0971: the change request whose Accept opened the company's edit window. */
  const [accepting, setAccepting] = useState<ChangeRequestView | null>(null);
  /** 🔒 Decision 5382: an employer or an address suggestion opens its own window prefilled, and its Save stamps the suggestion Accepted. */
  const [suggestionWindow, setSuggestionWindow] = useState<{ row: RecordSuggestionRow; stamp: () => Promise<void> } | null>(null);
  /** A write that went through and says so: a request filed or decided (decision 0971). The refusal banner stays the notice's. */
  const [confirmation, setConfirmation] = useState<string | null>(null);
  /** 🔒 What the Deal Pack's two acts say (decision 5400): a copy made in the good banner, a refusal in the notice's. */
  const packSaid = (said: DealPackOutcome) => {
    if (said === null) return;
    if (said.tone === 'good') setConfirmation(said.text);
    else setNotice(said.text);
  };
  /** Piece 22: New Purchase Order on a vendor's own page, opening on that vendor; the Work tile's one header action is its categories. */
  const [params] = useSearchParams();
  const gate = params.get('gate');
  /** 🔒 Row T2779: the record whose Company Files tile also lists its Deals' files; another record starts with the toggle off. */
  const [dealFilesOn, setDealFilesOn] = useState<string | null>(null);
  const dealFiles = recordType === 'CompanyRecord' && recordId !== undefined && dealFilesOn === recordId;
  const path = useMemo(() => {
    if (!recordId) return null;
    const query = new URLSearchParams();
    if (RECORD_PAGE_SPECS[recordType].pagesHistory && historyMonths > 1) query.set('historyMonths', String(historyMonths));
    if (dealFiles) query.set('dealFiles', '1');
    const text = query.toString();
    return `${PAGES_PATH}/${recordType}/${recordId}${text ? `?${text}` : ''}`;
  }, [recordType, recordId, historyMonths, dealFiles]);
  const { state, reload, denied, deniedReason } = useApi<PageAnswer>(path, apiBaseUrl);
  // 🔒 A merged record's old link opens the one that stays, in place of the old address (decision 7470).
  const movedTo = state.kind === 'ready' ? state.data.movedTo ?? null : null;
  // 🔒 The address's own parameters, the tile and the list's inputs, ride to the record that stays (T3459).
  const carried = params.toString();
  useEffect(() => {
    if (movedTo) navigate(`${recordHref(recordType, movedTo)}${carried ? `?${carried}` : ''}`, { replace: true });
  }, [movedTo, navigate, recordType, carried]);
  /** 🔒 The tile the address names is brought into view once the page is in (decision 9720; T3164): a Marks triangle opens a company at its Vendor or Sites tile. */
  const tileAsked = params.get(RECORD_TILE_PARAM);
  const pageReady = state.kind === 'ready';
  // 🔒 Only a key the page's own tiles carry is looked up, so an address cannot put a selector of its own into the query (T3459).
  const tileKnown = state.kind === 'ready' && tileAsked !== null && state.data.tiles.some((tile) => tile.key === tileAsked);
  useEffect(() => {
    if (!pageReady || !tileAsked || !tileKnown) return;
    document.querySelector(`[data-record-tile="${tileAsked}"]`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [pageReady, tileAsked, tileKnown, recordId]);
  /**
   * The colour of each tag on this record (piece 13). It is read here rather
   * than carried on the banner tile's rows, because the colour belongs to the
   * label and the tile row describes the application of it. A read that fails
   * leaves every chip on the default swatch, which is a legible chip and not a
   * missing one.
   */
  const tagColours = useRows<DrawnTag>(recordId ? `${PAGES_PATH}/tags/${recordType}/${recordId}` : null, apiBaseUrl);
  /**
   * 🔒 Each banner tag as its pill draws it (decisions 2260, 2261): the label's
   * color, its category and this record's note, read here; a tag the read has
   * not answered for yet draws on the default swatch with the plain tag icon.
   */
  const drawnTagOf = useCallback(
    (row: { id?: string | null; label: string }): DrawnTag => {
      const rows = tagColours.state.kind === 'ready' && Array.isArray(tagColours.state.data) ? tagColours.state.data : [];
      const read = rows.find((held) => held.id === row.id);
      return read ? { id: read.id, label: row.label, colour: read.colour ?? null, note: read.note ?? null, category: read.category ?? null } : { id: row.id ?? row.label, label: row.label };
    },
    [tagColours.state],
  );
  /** 🔒 The tag whose note window is open from its pill's menu (decision 2261), or null. */
  const [noteTag, setNoteTag] = useState<DrawnTag | null>(null);
  /** A Location's Verify address, held here so the answer outlives the reload that follows a write (decision 0338). */
  const locationVerify = useLocationVerify({ locationId: recordId, apiBaseUrl, onChanged: reload, onError: setNotice });
  /** 🔒 Use This Address (decision 7460): the address a service placed, held until Edit Location opens filled with it; the count keys the window so a second press opens on its own draft. */
  const [placedDraft, setPlacedDraft] = useState<{ fields: Record<string, unknown>; count: number } | null>(null);
  const spec = LIST_PAGES[recordType];
  /**
   * 🔒 Every per-type fact of this page, read from the one registry rather than
   * branched on here (T180; decision 0631). The page below draws what the record
   * type's own entry says it draws.
   */
  const pageSpec = RECORD_PAGE_SPECS[recordType];
  /** 🔒 The gear to the settings that govern this type's page, where its spec names them, in the title bar (decisions 2805, 2815; T1614). */
  const gear = useSettingsGear(pageSpec.settingsEntries);
  /** The Upcoming Events row being edited, which opens the Event modal on that event (decision 0108). */
  const [editingEvent, setEditingEvent] = useState<CalendarEventRow | null>(null);
  const close = useCallback(() => {
    setModal(null);
    setDropped([]);
    setUploadAct(null);
    setEditingEvent(null);
    setEditingAttribute(null);
    setEditingEmployment(null);
    setEditingOccupancy(null);
  }, []);
  /**
   * 🔒 The finished stage cards a person opened with Show on this visit (decision
   * 1216): held here, so the reload after a write keeps a card open, and keyed to
   * the record, so the next Deal and the next open start with every one folded.
   */
  const [shownCards, setShownCards] = useState<{ recordId: string; stages: ReadonlySet<string> }>({ recordId, stages: new Set() });
  const toggleShownCard = useCallback((stage: string, open: boolean) => {
    setShownCards((held) => {
      const stages = new Set(held.recordId === recordId ? held.stages : []);
      if (open) stages.add(stage);
      else stages.delete(stage);
      return { recordId, stages };
    });
  }, [recordId]);
  const saved = useCallback(() => {
    setNotice(null);
    reload();
  }, [reload]);

  /** A flag cleared from the banner keeps its row; only clearedDate moves (piece 13). */
  const clearFlag = async (flagId: string) => {
    const answer = await del(`${PAGES_PATH}/flags/${flagId}`, apiBaseUrl);
    if (!answer.ok) return setNotice(answer.message);
    reload();
  };

  /** 🔒 The replies beneath each note on the Internal Notes tile (decision 0671): written and deleted, then the notes read again. */
  const noteReplies = useNoteReplies(apiBaseUrl, reload);
  const sectionNotes = { personId, timeZone, replies: noteReplies };

  /** A tag comes off the record from its own chip, the way a flag clears; the label itself stays in Settings › CRM › Tags. */
  const removeTag = async (tagId: string) => {
    const answer = await del(`${PAGES_PATH}/tags/${recordType}/${recordId}/${tagId}`, apiBaseUrl);
    if (!answer.ok) return setNotice(answer.message);
    reload();
    tagColours.reload();
  };

  /**
   * A row ended from its tile (decision 0309): the deal team member, the deal
   * contact, the account role, the site role and the agreement each have their own
   * end route; a supplier row carries a status instead (decision 0337).
   *
   * 🔨 An employment, a Company contact, a site, an occupant and a contact method
   * are not here (decision 0589). Each of them records a date or deactivates a
   * login, so its end moved inside the row's own edit modal, which asks for the
   * day and confirms before posting the same route.
   */
  const endRow = useCallback(
    async (tile: EndableTile, row: TileRow) => {
      const id = row.id ?? '';
      const call =
        tile === 'dealTeam' ? post(`${DEALS_PATH}/${recordId}/team/${id}/end`, {}, apiBaseUrl)
        : tile === 'dealContacts' ? post(`${DEALS_PATH}/${recordId}/contacts/end`, { employmentId: id }, apiBaseUrl)
        : tile === 'accountRoles' ? post(`${LEADS_PATH}/companies/${recordId}/roles/end`, { roleTypeId: row.raw?.roleTypeId, employmentId: row.raw?.employmentId, startDate: row.raw?.startDate ?? null }, apiBaseUrl)
        : tile === 'roles' ? patch(`${PAGES_PATH}/sites/${recordId}/roles/${id}`, {}, apiBaseUrl)
        : post(`${NDA_PATH}/${id}/revoke`, {}, apiBaseUrl);
      const answer = await call;
      if (!answer.ok) setNotice(answer.message);
      else reload();
    },
    [apiBaseUrl, recordId, reload],
  );

  /** A desk check-in on a Site, written against the viewer's own employment (decision 0229). */
  const checkIn = useCallback(async () => {
    if (!employmentId) {
      setNotice(t('checkIn.noEmployment'));
      return;
    }
    /**
     * 🔒 The browser is asked where the person is, after its own consent prompt
     * (decision 0675). A refusal still checks in, with the coordinates left empty,
     * and says so: the row then reads no location shared.
     */
    const position = await readPosition();
    const answer = await post(`${PAGES_PATH}/check-ins`, { employmentId, siteId: recordId, source: 'Manual', ...(position.ok ? position.point : {}) }, apiBaseUrl);
    if (!answer.ok) setNotice(`${t('checkIn.refused')} ${answer.message}`);
    else {
      if (!position.ok) setNotice(`${t('checkIn.noCoordinates')} ${t(GEOLOCATION_REFUSAL_KEYS[position.refusal])}`);
      reload();
    }
  }, [apiBaseUrl, employmentId, recordId, reload]);

  const checkOut = useCallback(
    async (checkInId: string) => {
      const answer = await post(`${PAGES_PATH}/check-ins/${checkInId}/check-out`, {}, apiBaseUrl);
      if (!answer.ok) setNotice(answer.message);
      else reload();
    },
    [apiBaseUrl, reload],
  );

  /** The Lead's one-press actions (piece 20): each writes the status the action causes and nothing else. */
  const leadAct = useCallback(
    async (action: 'call-attempt' | 'convert-to-company' | 'release' | 'block' | 'temperature' | 'link', body: Record<string, unknown> = {}) => {
      const answer = await post(`${LEADS_PATH}/${recordId}/${action}`, body, apiBaseUrl);
      if (!answer.ok) setNotice(answer.message);
      else reload();
    },
    [apiBaseUrl, recordId, reload],
  );

  /** Won at Decision, and Reopen at Lost, are the page's own actions; Intent, Evaluation and Negotiation end on the card's Mark Complete (decision 0206). */
  const move = useCallback(
    async (action: 'won' | 'reopen') => {
      const answer = await post(`${DEALS_PATH}/${recordId}/${action}`, action === 'reopen' ? { stage: 'Intent' } : {}, apiBaseUrl);
      if (!answer.ok) setNotice(answer.message);
      else reload();
    },
    [apiBaseUrl, recordId, reload],
  );

  /** The Quick Deal hold's two answers (decision 0353); a rejection puts the Deal on the pipeline and opens the full form. */
  const decideQuick = useCallback(
    async (decision: 'Approve' | 'Reject') => {
      const answer = await post(`${DEALS_PATH}/${recordId}/quick-approval`, { decision }, apiBaseUrl);
      if (!answer.ok) return setNotice(answer.message);
      reload();
      if (decision === 'Reject') {
        setNotice(t('deal.approval.rejected'));
        setModal('editRecord');
      }
    },
    [apiBaseUrl, recordId, reload],
  );

  // 🔨 A viewer failing the record's Module sees Access Denied inside the shell, not a failed panel and not the record's name.
  if (denied) return <AccessDenied reason={deniedReason} />;

  return (
    <AsyncStatePanel state={state} emptyAction={spec.emptyAction} onRetry={reload}>
      {(answer) => {
        if (answer.movedTo) return null;
        const facts = answer.facts ?? {};
        const isOwnCompany = facts.isOwnCompany === true;
        // The Organization's own record and its sites are an administrator's to edit (decision 0270).
        const ownEditable = facts.ownCompanyEditable !== false;
        /**
         * 🔒 Decision 0971: whether this viewer writes on the record at all, as the
         * answer says. An outside login's own company page answers false, and the page
         * then draws no Add, no pencil, no End, no composer, no drop and no banner move.
         * An answer that says nothing is read as a writer's, as every page was before.
         */
        const writes = facts.writesRecord !== false;
        /**
         * 🔒 T355: whether the record's own Save would be taken, which is write on the record's
         * Module (and the own-record lock) as the answer says. The title's pencil is drawn from it,
         * so a login reading the module alone is never offered a form the server refuses.
         */
        const edits = facts.editsRecord !== false;
        /**
         * 🔨 A document is created on its own page, never in a modal (decision 0409): a Quote raised
         * here opens the Quote's own page carrying the record it was raised from. 🔒 One function for
         * the Quotes frame's + New Quote and the Negotiation card's Draft quote (decision 0666), so the
         * two buttons cannot open different pages.
         */
        const raiseQuote = () => navigate(`${LIST_PAGES.Quote.route}/${NEW_DOCUMENT_ID}?${newQuoteQuery(pageSpec, recordId, facts)}`);
        /** The action a tile's header carries; + Add Contact becomes + Hire on our own Company, hidden rather than refused (decision 0165). */
        const actionFor = (tile: TileAnswer): { label: string; onAdd?: () => void; route?: string } | null => {
          const kind = tile.action as TileActionKind | undefined;
          if (!kind || tile.masked || !tile.built || !writes) return null;
          // 🔒 A tile written through the record's own routes offers nothing to a viewer those routes refuse (decision 1000; T365).
          if (!edits && pageSpec.writeTiles?.includes(tile.key)) return null;
          // 🔒 + Hire opens the Hire window in HR (decisions 2690 and 3181).
          if (kind === 'addEmployed' && isOwnCompany) return { label: TILE_ACTION_LABELS.hire, route: HIRE_WINDOW_ROUTE };
          if (kind === 'addSite' && !ownEditable) return null;
          // 🔨 The credit route asks for the Accounting class and write on the Company module; the page carries the same answer, so no Edit is drawn for a viewer it would refuse (found in review, 2026-09-15).
          if (kind === 'editAccountStanding' && facts.maySetCreditTerms !== true) return null;
          // 🔒 A notes tile adds from the composer at the top of its list, never a header action (decision 0910).
          if (kind === 'addNote') return null;
          if (kind === 'checkIn') return { label: TILE_ACTION_LABELS.checkIn, onAdd: () => void checkIn() };
          if (kind === 'addAccountRole') return { label: TILE_ACTION_LABELS.addAccountRole, onAdd: () => setLeadModal('accountRole') };
          // The Work tile's vendor half (piece 22): the categories a vendor represents; a company that is no vendor offers nothing there.
          if (kind === 'addVendorRep' && facts.isVendor !== true) return null;
          // 🔨 The tile's + New Quote navigates through the one function the card's Draft quote calls (decisions 0409, 0666).
          if (kind === 'newQuote') return { label: TILE_ACTION_LABELS[kind], onAdd: raiseQuote };
          // 🔒 + New Work Order stands on a Won Project or Service Deal alone, and hands the Deal and its Account to the job's form (decision 10882; T3620).
          if (kind === 'newWorkOrder') {
            if (facts.isWon !== true || (facts.dealType !== 'Project' && facts.dealType !== 'Service') || typeof facts.companyId !== 'string') return null;
            return { label: TILE_ACTION_LABELS[kind], onAdd: () => navigate(`${LIST_PAGES.WorkOrder.route}/${NEW_DOCUMENT_ID}?dealId=${encodeURIComponent(recordId)}&companyId=${encodeURIComponent(facts.companyId as string)}`) };
          }
          return { label: TILE_ACTION_LABELS[kind], onAdd: () => setModal(kind) };
        };

        /**
         * 🔒 The composer at the top of a notes tile (decision 0910), where the tile's action is
         * Add Note and the viewer may write: one press writes the note through the modal's own
         * route with its defaults, and the page reads again so the note stands first in the list.
         */
        const composerFor = (tile: TileAnswer): ReactNode => {
          if (tile.action !== 'addNote' || tile.masked || !tile.built || !writes) return null;
          return (
            <NoteComposer
              name={`note-${tile.key}`}
              onAdd={async (text) => {
                const refused = await addNoteText(recordType, answer.recordId, text, apiBaseUrl);
                if (refused !== null) {
                  setNotice(refused);
                  return false;
                }
                saved();
                return true;
              }}
            />
          );
        };

        const companyId = typeof facts.companyId === 'string' ? facts.companyId : null;
        const releaseTargets = releaseCompanies(answer);
        // 🔨 Flags and tags are the record's state, read in the banner as pills and chips and never as a tile of their own (decision 0307).
        const bannerTile = answer.tiles.find((tile) => tile.placement === 'banner') ?? null;
        const flagRows = bannerTile?.rows ?? [];
        /** 🔒 The record's tags as their pills draw them, the label's color, category and this record's note on each (decisions 2260, 2261). */
        const bannerTags = flagRows.filter((row) => row.value === 'tag').map(drawnTagOf);
        /**
         * 🔒 Decision 4652 (5a): adding or removing a tag asks write on the record's Module, as the server's tag
         * routes do, so + Add Tag, each pill's cross and its note menu stand only where the answer says the viewer edits.
         */
        const tagsWrite = writes && edits;
        const tagControls = { writes: tagsWrite, onRemove: (tagId: string) => void removeTag(tagId), onEditNote: setNoteTag };
        /**
         * 🔒 One map from a panel key to the construction that draws it (T180;
         * decision 0631). The registry names a panel by key, because
         * `packages/shared` is DOM-free (decision 0242), and this map is total over
         * the union, so a key with no construction refuses to compile.
         */
        const panelFor: Readonly<Record<RecordPanelKey, () => ReactNode>> = {
          /* 🔒 Decision 0971: the Change requests tile, each row's Accept and Decline drawn where the row says this viewer decides it. */
          companyChangeRequests: () => (
            <ChangeRequestsPanel
              rows={answer.tiles.find((tile) => tile.key === CHANGE_REQUESTS_TILE)?.rows ?? []}
              zone={timeZone}
              dateFormat={dateFormat}
              apiBaseUrl={apiBaseUrl}
              onAccept={(request) => setAccepting(request)}
              onChanged={reload}
            />
          ),
          /* 🔒 Issue Stock under the Stock by Site rows (decision 1142), drawn for a writer of the Item alone, as the route asks. */
          itemIssueStock: () => (writes ? (
            <div data-item-issue-stock="true">
              <Button size={Size.Small} text={[t('stock.issue.action')]} actions={[() => setModal('issueStock')]} />
            </div>
          ) : null),
          /* 🔒 T1254 (decision 1940): every line naming the Item, read from its own route, in place of the tile's rows. */
          itemTransactions: () => <ItemTransactionsPanel key={answer.recordId} itemId={answer.recordId} apiBaseUrl={apiBaseUrl} />,
          /* 🔒 Decision 5210: the Person's texts on the company number, read from their own route under Internal Notes, with a box and Send. */
          /* 🔒 Decision 5382: the Suggestions tile, read from the record's own checks route with Accept and Dismiss. */
          recordSuggestions: () => (answer.recordType === 'PersonRecord' || answer.recordType === 'CompanyRecord' ? (
            <SuggestionsPanel key={answer.recordId} recordType={answer.recordType} recordId={answer.recordId} zone={timeZone} dateFormat={dateFormat} apiBaseUrl={apiBaseUrl} onOpenWindow={(row, stamp) => setSuggestionWindow({ row, stamp })} onWritten={reload} />
          ) : null),
          personTexts: () => <PersonTextsPanel key={answer.recordId} personId={answer.recordId} zone={timeZone} dateFormat={dateFormat} apiBaseUrl={apiBaseUrl} />,
          /* 🔒 Decision 6180: the Person's schools, the skills kept outside the catalog, and the LinkedIn page and assumed values, each read from its own route. */
          /* 🔒 Decision 6400: + Add School is the tile head's action, and its window is the page's own (the modal section below), so a folded tile still opens it. */
          personEducation: () => <PersonEducationPanel key={answer.recordId} personId={answer.recordId} apiBaseUrl={apiBaseUrl} />,
          /* 🔒 T2352: the Person's confidentiality grants, read from their own route, End on an open one for an administrator. */
          personConfidentialityRoles: () => <PersonConfidentialityPanel key={answer.recordId} personId={answer.recordId} zone={timeZone} dateFormat={dateFormat} apiBaseUrl={apiBaseUrl} />,
          personOtherSkills: () => <PersonOtherSkillsPanel key={answer.recordId} personId={answer.recordId} apiBaseUrl={apiBaseUrl} />,
          personProfile: () => <PersonProfilePanel key={answer.recordId} personId={answer.recordId} apiBaseUrl={apiBaseUrl} />,
          // The Item's image sits on General Information beside its fields, with Set, Replace and Remove (piece 17; decision 0269).
          itemImage: () => <ItemImageBlock itemId={answer.recordId} imageFileId={typeof facts.imageFileId === 'string' ? facts.imageFileId : null} apiBaseUrl={apiBaseUrl} onSet={() => setModal('setImage')} onRemoved={saved} onError={setNotice} />,
          // Verify address sits under the Location's facts (decision 0338): the tile keeps its rows and draws the panel beneath them.
          /* Verify address, then the pin beneath it with who placed it and when (decisions 0338, 0674); the pin's controls follow the photo's own write gate. */
          locationVerify: () => (
            <>
              <LocationVerifyPanel
                locationId={answer.recordId}
                verify={locationVerify}
                verificationStatus={typeof facts.verificationStatus === 'string' ? facts.verificationStatus : null}
                editable={answer.bannerAvatar?.editable === true}
                coordinatesMissing={facts.coordinatesMissing === true}
                onUsePlaced={(placed) => {
                  const held = editableFrom(answer).addressLines;
                  setPlacedDraft((was) => ({ fields: placedPrefill(placed, Array.isArray(held) ? held.map(String) : []), count: (was?.count ?? 0) + 1 }));
                  setModal('editRecord');
                }}
              />
              <LocationPinPanel
                locationId={answer.recordId}
                facts={{ pinLine: stringFact(facts.pinLine), pinMapsQuery: stringFact(facts.pinMapsQuery), pinnedBy: stringFact(facts.pinnedBy), pinnedAt: stringFact(facts.pinnedAt), pinnedFromLink: stringFact(facts.pinnedFromLink) }}
                zone={timeZone}
                dateFormat={dateFormat}
                apiBaseUrl={apiBaseUrl}
                editable={answer.bannerAvatar?.editable === true}
                onChanged={reload}
              />
            </>
          ),
          /* 🔒 Decision 0739: the jurisdictions the site's address resolves to, the coverage, the warnings and the exemption forms with their On File pills; recording a certificate is in the Exempt Customer Steps window (decision 2851). */
          /* 🔒 Decision 12540: the trees standing at the site, with the node page beside them, in place of the Assets tile's rows. */
          siteAssets: () => (
            <Suspense fallback={null}>
              <SiteAssetsPanel key={`site-assets-${answer.recordId}`} siteId={answer.recordId} apiBaseUrl={apiBaseUrl} />
            </Suspense>
          ),
          siteTaxJurisdiction: () => <SiteTaxJurisdictionPanel key={`tax-jurisdiction-${answer.recordId}-${(answer.tiles.find((tile) => tile.key === 'certificates')?.rows ?? []).map((row) => row.id).join(',')}`} siteId={answer.recordId} apiBaseUrl={apiBaseUrl} />,
          /* 🔒 Decision 12503: the plant feed's connector, its state, its outages and its tag maps, read from its own route by a holder of the asset register's write. */
          sitePlantFeed: () => <PlantFeedPanel key={`plant-feed-${answer.recordId}`} siteId={answer.recordId} siteName={answer.title} apiBaseUrl={apiBaseUrl} />,
          /* 🔨 Piece 24: the Vendor Tile, revealed by the Is Vendor flag and gated to the Accounting and Admin
             roles by the route it reads. It draws its own two status pills rather than a Label/Value grid. */
          vendorTax: () => (
            <Suspense fallback={null}>
              <VendorTaxPanel companyId={answer.recordId} isVendor={facts.isVendor === true} apiBaseUrl={apiBaseUrl} />
            </Suspense>
          ),
          /* 🔒 Decision 2080: the customer's message rules on its Field Service tile, and one Deal's under its Work Orders, each read from its own route. */
          companyJobMessages: () => <JobMessageRules key={`company-${answer.recordId}`} companyId={answer.recordId} apiBaseUrl={apiBaseUrl} />,
          dealJobMessages: () => <JobMessageRules key={`deal-${answer.recordId}`} dealId={answer.recordId} apiBaseUrl={apiBaseUrl} />,
          /* The Work Order's three constructions (piece 22): the labour lines with their workers as chips, the parts fitted and the travel line. */
          /* 🔒 Decision 6020: Job Progress, read-only, from the page answer's own block. */
          workOrderProgress: () => (answer.workOrderProgress ? <WorkOrderProgress progress={answer.workOrderProgress} /> : null),
          workOrderLines: () => (answer.workOrder ? <WorkOrderLines document={answer.workOrder} apiBaseUrl={apiBaseUrl} onChanged={saved} onError={setNotice} /> : null),
          workOrderMaterials: () => (answer.workOrder ? <WorkOrderMaterials document={answer.workOrder} apiBaseUrl={apiBaseUrl} onChanged={saved} onError={refuse} /> : null),
          workOrderTravel: () => (answer.workOrder ? <WorkOrderTravel document={answer.workOrder} apiBaseUrl={apiBaseUrl} onChanged={saved} onError={setNotice} /> : null),
          /* 🔒 Decision 1908: the job's visits, read by the tile itself, with Book Another Visit and Cancel. */
          workOrderVisits: () => (answer.workOrder ? <WorkOrderVisits workOrderId={answer.workOrder.id} companyId={answer.workOrder.companyId} siteId={answer.workOrder.siteId} apiBaseUrl={apiBaseUrl} onChanged={saved} /> : null),
          /* 🔒 Decision 2910: the job's Documents, each visit's Service Report with its state and every invoice raised from the job. */
          workOrderDocuments: () => (answer.workOrder ? <WorkOrderDocuments workOrderId={answer.workOrder.id} apiBaseUrl={apiBaseUrl} onError={setNotice} /> : null),
          /* 🔒 Decision 1908: every time entry on the job, read by the tile itself, with Record Time and Raise a Correction. */
          workOrderLabour: () => (answer.workOrder ? <WorkOrderLabour workOrderId={answer.workOrder.id} apiBaseUrl={apiBaseUrl} onChanged={saved} /> : null),
          /* 🔒 Decision 0765: the Deal's team as it stood when the job was created, in its own tile. */
          workOrderDealTeam: () => <DealTeamPanel team={answer.dealTeam ?? null} timeZone={timeZone} bare />,
          /* 🔒 The moves the Work Order transition table allows from the stored status, laid out by the one action bar (T168, T181). */
          workOrderFooter: () => (answer.workOrder ? <WorkOrderFooter document={answer.workOrder} apiBaseUrl={apiBaseUrl} timeZone={timeZone} offersRateWorking={facts.offersRateWorking === true} canApprove={answer.canApprove === true} onChanged={saved} onError={setNotice} /> : null),
          // The Lead page's own constructions (piece 20): the three StageCards, the ladder and its rail.
          leadStageCards: () => <LeadStageCards leadId={answer.recordId} cards={answer.cards ?? []} writes={edits} apiBaseUrl={apiBaseUrl} onChanged={saved} onError={setNotice} />,
          leadProgress: () => <LeadProgress cards={answer.cards ?? []} />,
          leadRail: () => <LeadRail cards={answer.cards ?? []} />,
          // The Deal page's own constructions (piece 19; figures 81 to 83), which draw themselves rather than a Tile item list.
          dealProgress: () => <DealProgress rows={answer.progress ?? []} stageLabel={String(facts.stageLabel ?? '')} boardRoute="/deals?view=board" escalates={facts.escalates === true} nextAction={nextActionRows(answer)} />,
          dealRail: () => <ProgressRail rows={answer.progress ?? []} />,
          /* 🔒 The Deal banner's summary column (decision 0665): the value, and the Next Action tile's own rows capped at three lines. */
          dealBannerColumn: () => <DealBannerColumn valueText={typeof facts.dealValueText === 'string' ? facts.dealValueText : null} valueSource={dealValueSourceOf(facts.dealValueSource)} budgetTooLow={facts.budgetTooLow === true} timing={{ expectedQuoteDate: stringFact(facts.expectedQuoteDate), expectedStartDate: stringFact(facts.expectedStartDate), expectedFinishDate: stringFact(facts.expectedFinishDate) }} />,
          dealStageCard: () => (answer.stageCard
            ? <DealStageCard dealId={answer.recordId} card={answer.stageCard} bomLists={answer.bomLists ?? []} rfqs={answer.rfqs ?? []} authority={answer.authority ?? []} budget={answer.budget ?? null} timing={answer.timing ?? null} latestQuote={answer.latestQuote ?? null} onDraftQuote={raiseQuote} seesCost={facts.seesCost === true} listsEditable={facts.isLost !== true} bomLocked={facts.isDone === true && facts.isLost !== true ? 'done' : null} writes={edits} apiBaseUrl={apiBaseUrl} onChanged={saved} onError={setNotice} onNotice={setNotice} onRfq={() => setModal('newRfq')} personId={personId} timeZone={timeZone} sectionReplies={noteReplies} />
            : <p className={styles.hint} data-stage-card-note={String(facts.stage ?? '')}>{t('deal.card.lostNote')}</p>),
          dealStages: () => dealStages(answer, facts, apiBaseUrl, saved, setNotice, setNotice, { onRfq: () => setModal('newRfq'), onWon: () => void move('won'), onLost: () => setLostOpen(true), onDraftQuote: raiseQuote }, sectionNotes, edits, { stages: shownCards.recordId === recordId ? shownCards.stages : new Set(), toggle: toggleShownCard }),
          /* 🔒 Next Action as the page's own banner (decision 1219): the derived list the answer carries, the first named and the rest counted. */
          dealNextAction: () => (
            <>
            <DealNextActionBanner
              dealId={answer.recordId}
              personId={personId}
              rows={nextActionRows(answer)}
              stage={typeof facts.stage === 'string' ? facts.stage : null}
              actions={{
                // 🔒 The ladder's own moves (decision 1935), offered only where the ladder itself offers them.
                // 🔒 The request page's moves are its band's and Name the Owner's (decision 2293), so it offers neither of these.
                ...(edits && facts.isLost !== true && facts.isDone !== true && facts.dealPage !== 'request' ? { newRfq: () => setModal('newRfq'), draftQuote: raiseQuote } : {}),
                ...(facts.atDecision === true ? { markWon: () => void move('won'), markLost: () => setLostOpen(true) } : {}),
                openProgress: openDealProgress,
              }}
            />
            {/* 🔒 Where the job stands, one line under the banner on a request (decision 1913). */}
            {answer.requestStatus ? <DealRequestStatus view={answer.requestStatus} /> : null}
            </>
          ),
          /* 🔒 Earlier Month at the foot of History, the tile it pages (decision 1215); it stood in the banner's action row, far from the rows it loads. */
          dealHistoryFoot: () => (
            <div className={styles.historyFoot} data-deal-history-foot="true">
              <Button size={Size.Small} text={[t('deal.action.earlierMonth')]} actions={[() => setHistoryMonths((n) => n + 1)]} />
            </div>
          ),
          /* 🔨 The panel is drawn only where the route offers it, so a reader with no floor working sees the tile's own rows and nothing more (piece 19). */
          dealFloorWorking: () => (facts.offersFloorWorking === true ? <DealFloorWorking dealId={answer.recordId} offered apiBaseUrl={apiBaseUrl} /> : null),
        };
        /**
         * 🔒 On a document page the tile the entry names is the identifying facts row
         * at the top of the Details view and is not drawn again as a tile (T170, T181).
         */
        const factsTileKey = pageSpec.documentPage?.factsTile ?? null;
        /** Each tile's items and action, which a merged tile gathers from the tiles it names (decision 1217). */
        const gathered = new Map<string, { label: string; items: readonly TileItem[]; action: ReturnType<typeof actionFor>; rows: number }>();
        const tiles: CanvasTile[] = answer.tiles.filter((tile) => tile.placement !== 'banner' && tile.key !== factsTileKey && !tile.masked).map((tile) => {
          const action = actionFor(tile);
          const composer = composerFor(tile);
          const fileHref = (fileId: string) => `${apiBaseUrl}${FILES_PATH}/${fileId}/stream`;
          // Every row control comes from the one registry (decision 0309); the page supplies the handlers and never branches on a tile key.
          // 🔒 Decision 0971: a viewer the answer says writes nothing is handed no row control at all.
          // 🔒 T365: a tile written through the record's own routes hands no row control to a viewer those routes refuse.
          const rowActions = !writes || (!edits && pageSpec.writeTiles?.includes(tile.key)) ? {} : rowActionsFor(tile, {
            personId,
            endRow: (kind, row) => void endRow(kind as EndableTile, row),
            checkOut: (id) => void checkOut(id),
            release: (row) => setRelease({ fileId: row.id ?? '', fileName: row.label }),
            shareFile: (row) => setShare({ fileId: row.id ?? '', fileName: row.label }),
            editAttribute: (row) => { setEditingAttribute(row.id ?? null); setModal('addAttribute'); },
            editSupplier: (row) => { setEditingSupplier({ id: row.id ?? '', vendorName: row.label, vendorPartNumber: typeof row.raw?.vendorPartNumber === 'string' ? row.raw.vendorPartNumber : null, minimumOrderQuantity: typeof row.raw?.minimumOrderQuantity === 'number' ? row.raw.minimumOrderQuantity : null, status: asItemSupplierStatus(row.raw?.status) ?? 'Active', ended: row.raw?.ended === true }); setModal('addSupplier'); },
            editNote: (row) => { setEditingNote({ id: row.id ?? '', draft: noteDraftFrom(row) }); setModal('editNote'); },
            endRequirement: (row) => setEndingRequirement({ id: row.id ?? '', label: row.label, value: row.value ?? '', removable: row.raw?.removable === true }),
            editCertificate: editCertificateRow,
            endCertificate: (row) => setEndingCertificate({ id: row.id ?? '', label: row.label, value: row.value ?? '' }),
            removeCertificate: (row) => setRemovingCertificate({ id: row.id ?? '', label: row.label, value: row.value ?? '' }),
            moveCertificate: (row) => setMovingCertificate({ id: row.id ?? '', label: row.label, value: row.value ?? '' }),
            editTermsSet: (row) => setChoosingTermsSet({ setId: typeof row.raw?.setId === 'string' ? row.raw.setId : null }),
            // 🔒 Decision 2972: the mailbox row carries what its window opens on, the version it read among them.
            editMailbox: (row) => {
              const kind = row.raw?.mailboxKind;
              if (!isCompanyMailboxKind(kind) || !row.id) return;
              setEditingMailbox({ id: row.id, version: typeof row.raw?.version === 'number' ? row.raw.version : 0, kind, label: typeof row.raw?.label === 'string' ? row.raw.label : null, email: typeof row.raw?.value === 'string' ? row.raw.value : '' });
              setModal('addMailbox');
            },
            // 🔒 Decision 7501: the Company page's Owned By lines and Subsidiaries rows, each row carrying what End Ownership opens on.
            ...(answer.recordType === 'CompanyRecord' ? {
              addOwner: () => setOwnershipWindow({ mode: 'add' }),
              endOwnership: (row: TileRow) => { const ownership = ownershipEndingOf(row.id, row.raw); if (ownership) setOwnershipWindow({ mode: 'end', ownership }); },
            } : {}),
            // 🔒 The row names its link and the server has said this viewer may delete it; the window asks why (T728; decision 1500).
            deleteLink: (link) => setDeletingLink(link),
            // 🔒 The row carries the row it folds into, which the server offered it on (decision 1920).
            foldSite: (row) => setFoldingSite({ fromId: row.id ?? '', fromLabel: [row.label, row.detail].filter(Boolean).join(' · '), intoId: typeof row.raw?.foldIntoId === 'string' ? row.raw.foldIntoId : '', intoLabel: typeof row.raw?.foldIntoLabel === 'string' ? row.raw.foldIntoLabel : '' }),
            editContactMethod: (row) => {
              setEditingContact({ id: row.id ?? '', kind: String(row.raw?.kind ?? 'Other') as ContactMethodEdit['kind'], value: typeof row.raw?.value === 'string' ? row.raw.value : '', extension: typeof row.raw?.extension === 'string' ? row.raw.extension : null, label: typeof row.raw?.label === 'string' ? row.raw.label : null, isPrimary: row.raw?.isPrimary === true, jobLabel: typeof row.raw?.jobLabel === 'string' ? row.raw.jobLabel : null });
              setModal('addContactMethod');
            },
            // The employment's own pencil: the row already carries what the modal opens on, so the pencil asks the API for nothing.
            editEmployment: (row) => {
              setEditingEmployment({
                employmentId: typeof row.raw?.employmentId === 'string' ? row.raw.employmentId : '',
                // The row's label is the other end from either page: the company on the Person page, the person on the Company page.
                otherEnd: row.label,
                title: typeof row.raw?.title === 'string' ? row.raw.title : '',
                workEmail: typeof row.raw?.workEmail === 'string' ? row.raw.workEmail : '',
                workPhone: typeof row.raw?.workPhone === 'string' ? row.raw.workPhone : '',
                workPhoneExtension: typeof row.raw?.workPhoneExtension === 'string' ? row.raw.workPhoneExtension : '',
                startDate: typeof row.raw?.startDate === 'string' ? row.raw.startDate : '',
                endDate: typeof row.raw?.endDate === 'string' ? row.raw.endDate : null,
                // 🔒 Decisions 6446 and 6660: the positions the row carries, which the window edits as one list.
                positions: positionListOf(row.raw?.positionsJson),
              });
              setModal('editEmployment');
            },
            // 🔒 Set level on an open position at our own Company: the row carries the level it holds, so the window asks the API only for the active levels (T726).
            setLevel: (row) => setSettingLevel({
              employmentId: typeof row.raw?.employmentId === 'string' ? row.raw.employmentId : '',
              label: row.label,
              skillLevelId: typeof row.raw?.skillLevelId === 'string' ? row.raw.skillLevelId : null,
              skillLevelName: typeof row.raw?.skillLevelName === 'string' ? row.raw.skillLevelName : null,
            }),
            // The occupancy's own pencil, on both tiles that draw it (decision 0589).
            editOccupancy: (row) => {
              setEditingOccupancy({
                siteId: row.id ?? '',
                label: row.label,
                name: typeof row.raw?.name === 'string' ? row.raw.name : '',
                suite: typeof row.raw?.suite === 'string' ? row.raw.suite : '',
                startDate: typeof row.raw?.startDate === 'string' ? row.raw.startDate : '',
                endDate: typeof row.raw?.endDate === 'string' ? row.raw.endDate : null,
              });
              setModal('editOccupancy');
            },
            editTeamMember: (row) => {
              // 🔨 The owner's remainder is the server's own answer (decisions 0114, 0595). It was
              // summed here over the tile's rows, and the tile holds only the shares this viewer may
              // see, so a viewer refused one member's share was shown a remainder that was too large.
              const ownerRemainder = typeof row.raw?.ownerRemainder === 'number' ? row.raw.ownerRemainder : null;
              setEditingMember({ id: row.id ?? '', personName: row.label, roleTypeId: typeof row.raw?.roleTypeId === 'string' ? row.raw.roleTypeId : '', isPrimary: row.raw?.isPrimary === true, shareRevealed: row.raw?.shareRevealed === true, commissionSharePercent: typeof row.raw?.commissionSharePercent === 'number' ? row.raw.commissionSharePercent : null, ownerRemainder });
              setModal('addTeamMember');
            },
          });
          // 🔨 The window opens on the raw description and the version the row carries, never on the
          // joined detail line: the line already holds both, joined for reading (decision 0605).
          const onFileEdit = (row: TileRow) => { setFileDetails({ id: row.id ?? '', name: row.label, description: typeof row.raw?.description === 'string' ? row.raw.description : '', version: typeof row.raw?.version === 'number' ? row.raw.version : null, typeName: row.value ?? null }); setModal('fileDetails'); };
          // 🔒 Former sites fold for every reader, writer or not (decision 1920); a tile with no former row ignores the toggle.
          const former = { formerOpen: openFormer.has(tile.key), onToggleFormer: () => setOpenFormer((held) => { const next = new Set(held); if (next.has(tile.key)) next.delete(tile.key); else next.add(tile.key); return next; }) };
          // 🔒 A document row the viewer cannot open shows the page's Permission denied banner when pressed, for every reader (decision 10370, building 10240).
          const onRefused = () => setNotice(t('record.tile.permissionDenied'));
          const draw = (drawn: TileAnswer) => itemsFor(drawn, timeZone, writes ? { ...rowActions, fileHref, onFileEdit, ...noteReplies, ...former, onRefused } : { fileHref, ...former, onRefused }, dateFormat);
          // 🔒 The Deal's contacts carry the Authority tag on their own rows, read from the same answer the Intent card's Authority row draws (decision 1405).
          const items = answer.recordType === 'Deal' && tile.key === 'dealContacts'
            ? withAuthorityTags(tile, draw, { dealId: answer.recordId, authority: answer.authority ?? [], editable: writes && facts.isLost !== true, apiBaseUrl, onChanged: saved, onError: setNotice, contactTags: answer.contactTags ?? {} })
            : draw(tile);
          // The Deal page's two constructions draw themselves rather than a Tile item list (piece 19; figures 81 to 83).
          // Upcoming Events is the Condensed Event Booking row (piece 4), which takes the whole event; the tile's header stands in for the band.
          const eventRows = (tile.draws === 'eventBooking' ? (tile.rows ?? []) : []).map((row) => row.event).filter((event): event is CalendarEventRow => Boolean(event));
          /* 🔒 The construction that draws this tile instead of its item list, where the record type's own entry names one (T180). */
          const panelKey = pageSpec.tilePanels[tile.key];
          const custom = eventRows.length > 0
            ? (
                /* 🔒 Five rows stand and the rest scroll inside the tile (T149, product owner,
                   2026-09-21). 🔨 T166 drops the count line that used to stand beneath them
                   (product owner, 2026-09-22: the "5 of x" line is "useless data") — the scroll
                   bar alone is what says there is more. */
                <div className={styles.eventScroll} data-event-list-scroll={String(eventRows.length)}>
                  <EventBookingList
                    band={false}
                    events={eventRows.map((row) => toSummary(row))}
                    onEdit={(summary) => {
                      const row = eventRows.find((r) => r.id === summary.id) ?? null;
                      setEditingEvent(row);
                      setModal('addEvent');
                    }}
                  />
                </div>
              )
            : panelKey !== undefined
            ? panelFor[panelKey]()
            : null;
          // 🔒 The panel hanging under this tile's rows, where the record type's own entry names one (T180).
          const underKey = pageSpec.tileUnder[tile.key];
          const underPanel = underKey === undefined ? null : panelFor[underKey]();
          // 🔒 The panel beside this tile's rows, where the record type's own entry names one (T180): the Item's image is the one today.
          const asideKey = pageSpec.tileAside[tile.key];
          const itemImage = asideKey === undefined ? null : panelFor[asideKey]();
          gathered.set(tile.key, { label: tile.label, items, action, rows: (tile.rows ?? []).length });
          /**
           * 🔒 An empty tile is its title row alone (decision 6700): the rows drew only the empty note, and
           * the tile carries no composer and no construction of its own. Texts and Internal Notes are never
           * empty here, and a facts tile with its Not recorded pairs has rows, so it stays a card.
           */
          const only = items.length === 1 ? items[0] : undefined;
          const slim = tile.built && !custom && !underPanel && !itemImage && !composer && only?.type === 'note' && only.text === t('record.tile.empty');
          return {
            slim,
            placement: { id: tile.key, span: tile.span ?? 1, height: tile.height ?? 1, locked: true } as TilePlacement,
            bounds: { minWidth: RECORD_TILE_MIN_WIDTH, maxWidth: RECORD_TILE_MAX_WIDTH },
            title: tile.label,
            render: () => (
              <div className={styles.slot} data-record-tile={tile.key} data-tile-masked={tile.masked ? 'true' : 'false'} data-tile-owner={tile.owner} data-tile-draws={tile.draws ?? ''}>
                <Tile
                  title={tile.label}
                  items={custom ? [] : items}
                  minWidth={RECORD_TILE_MIN_WIDTH}
                  maxWidth={RECORD_TILE_MAX_WIDTH}
                  {...(custom ? { children: custom } : underPanel ? { children: underPanel } : {})}
                  {...(itemImage ? { aside: itemImage } : {})}
                  {...(tile.key === 'flags' ? { resolve: flagResolver } : {})}
                  {...(tile.collapsible ? { collapsible: true } : {})}
                  {...(tile.pageSize ? { pageSize: tile.pageSize } : {})}
                  {...(tile.windowRows ? { windowRows: tile.windowRows } : {})}
                  {...(action ? { addLabel: action.label, ...(action.route ? { addRoute: action.route } : action.onAdd ? { onAdd: action.onAdd } : {}) } : {})}
                  {...(answer.recordType === 'Deal' && tile.key === 'dealTeam' && writes && edits && facts.isWon !== true && facts.isLost !== true ? { secondAction: { label: t('deal.owner.action'), onAct: () => setModal('changeOwner') } } : {})}
                  {...(answer.recordType === 'SiteRecord' && tile.key === 'certificates' ? { secondAction: { label: t('site.exemptSteps.title'), onAct: () => setStepsOpen(true) } } : {})}
                  {...(answer.recordType === 'PersonRecord' && tile.key === 'employments' && facts.uploadsEmploymentDocuments === true ? { secondAction: { label: t('employment.documents'), onAct: () => { setUploadAct('employment'); setModal('upload'); } } } : {})}
                  {...(answer.recordType === 'CompanyRecord' && tile.key === 'companyFiles' ? { secondAction: { label: t(dealFiles ? 'file.dealFiles.hide' : 'file.dealFiles.show'), onAct: () => setDealFilesOn(dealFiles ? null : answer.recordId) } } : {})}
                  {...(composer ? { composer } : {})}
                  {...(slim ? { empty: t('record.tile.empty') } : {})}
                  {...(tile.action === 'upload' && tile.built && !tile.masked && writes
                    ? {
                        onDropFiles: (files: readonly File[]) => {
                          setDropped(files);
                          setModal('upload');
                        },
                      }
                    : {})}
                />
              </div>
            ),
          };
        });
        /**
         * 🔒 A tile the page draws out of several (decision 1217): the Deal's
         * Documents tile, a section per kind with its own count and newest first
         * as each list arrives, the kinds with nothing yet named in one closing
         * sentence, and the first kind's action as its own.
         */
        for (const [key, merge] of Object.entries(pageSpec.staged?.merges ?? {})) {
          const parts = merge.from.map((from) => gathered.get(from)).filter((part): part is NonNullable<typeof part> => part !== undefined);
          if (parts.length === 0) continue;
          const title = t(merge.titleKey);
          const filled = parts.filter((part) => part.rows > 0);
          const empty = parts.filter((part) => part.rows === 0).map((part) => part.label);
          const items: TileItem[] = filled.flatMap((part) => part.items.map((item) => (item.type === 'fullWidthRow' ? { ...item, heading: line(part.label, String(part.rows)) } : item)));
          const closing = filled.length === 0
            ? t('deal.documents.none')
            : empty.length === 0 ? null
              : line(t('deal.documents.noneOf'), empty.length === 1 ? empty[0]! : `${empty.slice(0, -1).join(', ')} ${t('deal.documents.or')} ${empty[empty.length - 1]!}`, t('deal.documents.yet'));
          // 🔒 One control per section that carries one, the first in the header's add place and the next in the Tile's second place (decision 10882; T3620).
          const [action, second] = parts.map((part) => part.action).filter((a): a is NonNullable<typeof a> => a !== null);
          const secondAct = second ? (second.onAdd ?? (second.route ? () => navigate(second.route!) : null)) : null;
          tiles.push({
            placement: { id: key, span: 1, height: 1, locked: true } as TilePlacement,
            bounds: { minWidth: RECORD_TILE_MIN_WIDTH, maxWidth: RECORD_TILE_MAX_WIDTH },
            title,
            render: () => (
              <div className={styles.slot} data-record-tile={key} data-tile-masked="false" data-tile-owner="here" data-tile-draws="merged">
                <Tile
                  title={title}
                  count={filled.reduce((sum, part) => sum + part.rows, 0)}
                  items={items}
                  minWidth={RECORD_TILE_MIN_WIDTH}
                  maxWidth={RECORD_TILE_MAX_WIDTH}
                  collapsible
                  {...(closing ? { children: <p className={styles.mergedClosing} data-merged-closing={key}>{closing}</p> } : {})}
                  {...(action ? { addLabel: action.label, ...(action.route ? { addRoute: action.route } : action.onAdd ? { onAdd: action.onAdd } : {}) } : {})}
                  {...(second && secondAct ? { secondAction: { label: second.label, onAct: secondAct } } : {})}
                />
              </div>
            ),
          });
        }
        const pills = bannerPills(recordType, facts);
        /**
         * 🔒 The one line under the name, from the one function (T176). On a Company
         * and a Person it is the classification pills the server computed (T175);
         * on every other record type it is the record type's own name.
         *
         * 💰 The classifications drew as a second group of small pills beside the
         * name until 2026-09-22, so a Company said Customer twice — once there and
         * once in the line beneath, which read Company and said nothing (decision
         * 0335, amended).
         */
        const kindLine = pageKindLine({ page: 'record', recordType, classifications: answer.classifications ?? null, parent: answer.kindParent ?? null, partNumber: typeof facts.sku === 'string' ? facts.sku : null });
        /**
         * 🔒 The kind line as the banner draws it: the pills alone, the type's name, or
         * on a Deal the type with its customer as a link and the Deal's own pills
         * after it (decision 0722).
         */
        const kindNode: ReactNode = kindLine.kind === 'pills'
          ? <BannerPills pills={kindLine.pills} size={Size.Small} attribute="data-record-classifications" />
          : kindLine.kind === 'linked'
            ? (
              <span className={styles.kindLinked} data-record-kind-parent={kindLine.parent.route}>
                <span>{line(kindLine.text, '·')}</span>
                {/* 🔒 Decision 2153: the customer is a link only where this viewer's read of it would succeed. */}
                {kindLine.parent.route ? <Link className={styles.kindLinkOnBanner} to={kindLine.parent.route}>{kindLine.parent.text}</Link> : <span data-record-kind-parent-plain="">{kindLine.parent.text}</span>}
                {kindLine.pills.length > 0 ? <BannerPills pills={kindLine.pills} size={Size.Small} attribute="data-record-classifications" /> : null}
                {/* 🔒 A cloned Deal's chip links back to the Deal it was cloned from (decision 1482). */}
                {typeof facts.clonedFromDealId === 'string' ? (
                  <Link className={styles.clonedFromChip} to={`/deals/${facts.clonedFromDealId}`} data-deal-cloned-from={facts.clonedFromDealId}>
                    <Pill text={fill(t('deal.clone.from'), { name: String(facts.clonedFromDealName ?? '') })} foreground="var(--status-inactive-text)" background="var(--status-inactive-bg)" size={Size.Small} asTyped />
                  </Link>
                ) : null}
              </span>
            )
            : kindLine.text;
        /** 🔒 The custom layout this record type draws instead of the canvas, where its entry names one (decision 0293; T180). */
        const staged = pageSpec.staged;
        const editable = EDIT_FIELDS[recordType].length > 0;
        const contactsTile = answer.tiles.find((tile) => tile.key === 'contacts');
        /**
         * 🔒 The two document views, where the record type's entry says its page is
         * one (T181, on decision 0612). The record's own tiles are the Details view,
         * the tile the entry names carries the identifying facts above them, and the
         * footer's controls come from the registry like any other panel.
         */
        const documentPage = pageSpec.documentPage;
        const factsTile = documentPage ? answer.tiles.find((tile) => tile.key === documentPage.factsTile) ?? null : null;
        const information: BannerFact[] = (factsTile?.rows ?? []).map((row) => ({ label: row.label, value: row.value ?? null, ...(row.route ? { route: row.route } : {}), ...(row.money ? { money: true } : {}) }));

        /**
         * 🔒 One map from a banner action's key to the control that draws it (T180;
         * decision 0631). The registry names the action and the reading of the
         * page's own facts that offers it; the control itself is here.
         */
        const bannerActionFor: Readonly<Record<RecordActionKey, () => ReactNode>> = {
          /* Piece 27, decision 0223: a customer's statement for a period, computed live and snapshotted only when it is sent. */
          companyStatements: () => <span data-record-action="statements"><Button size={Size.Small} text={[t('record.statements')]} actions={[() => navigate(`${STATEMENTS_ROUTE}?companyId=${encodeURIComponent(answer.recordId)}`)]} /></span>,
          /* 🔨 + New Deal from the Account itself, never + New Quote (decision 0497). Work enters
             at the Deal and runs Deal → RFQ → Quote → Sales Order → purchase or work order →
             invoice or bill; a quote raised straight off an account started halfway down that
             line, with nothing above it to say what the work was. The Deal raises the quote. */
          /* 🔒 It opens the New Deal page with this account filled in, never a window (decision 2510): creating a Deal leaves for the Deal. */
          companyNewDeal: () => <Button size={Size.Small} text={[TILE_ACTION_LABELS.newDeal]} actions={[() => navigate(newDealRoute(answer.recordId), { state: { accountName: answer.title } satisfies NewDealArrival })]} />,
          /* 🔒 Decision 0971: an outside login's one control on its own company page, offered by the answer's own fact. */
          companyRequestChange: () => <RequestChangeButton companyId={answer.recordId} apiBaseUrl={apiBaseUrl} onFiled={() => { reload(); setConfirmation(t('changeRequest.filed')); }} />,
          /* 🔨 No Won button here and no Mark Lost at Decision (decision 0595): the stage that ends the deal carries both on its own card. */
          dealLost: () => <Button size={Size.Small} color={Color.Inactive} text={[t('deal.action.lost')]} actions={[() => setLostOpen(true)]} />,
          /* Piece 32 part b (decision 0791): the composer opens on a message tagged to this record, from the module's mailbox where its rule names one. */
          // 🔒 Decision 1468: the complaint is raised for the viewer's own employment, and its window asks before a word is typed.
          personRaiseComplaint: () => <RaiseComplaintAction apiBaseUrl={apiBaseUrl} />,
          /* 🔒 Decision 12660: Look Up Manufacturer, the seam asked for this Item alone; what it finds opens the lookup's Step Through. */
          itemLookUp: () => {
            const editable = editableFrom(answer);
            const text = (value: unknown) => (typeof value === 'string' && value !== '' ? value : null);
            return <LookUpManufacturerButton itemId={answer.recordId} manufacturer={text(editable.manufacturerCompanyIdLabel)} partNumber={text(editable.manufacturerPartNumber)} apiBaseUrl={apiBaseUrl} onWrote={saved} />;
          },
          sendEmail: () => <span data-record-action="send-email"><Button size={Size.Small} text={[t('mail.compose.aboutRecord')]} actions={[() => navigate(`${MAIL_ROUTE}?${new URLSearchParams({ compose: 'record', recordType, recordId: answer.recordId }).toString()}`)]} /></span>,
          dealReopen: () => <Button size={Size.Small} color={Color.Inactive} text={[t('deal.action.reopen')]} actions={[() => void move('reopen')]} />,
          /* 🔒 Clone, in every stage (decision 1482); the window asks the Account and what comes along. */
          dealClone: () => <span data-record-action="clone"><Button size={Size.Small} text={[t('deal.action.clone')]} actions={[() => setCloneOpen(true)]} /></span>,
          /* 🔒 The Deal Pack under More (decision 5400): the text to the clipboard, or the zip saved; each says how it went in the page's own banners. */
          dealCopyForClaude: () => <span data-record-action="copy-for-claude"><Button size={Size.Small} text={[t('deal.pack.copy')]} actions={[() => void copyDealPack(answer.recordId, apiBaseUrl).then(packSaid)]} /></span>,
          dealDownloadPack: () => <span data-record-action="download-deal-pack"><Button size={Size.Small} text={[t('deal.pack.download')]} actions={[() => void downloadDealPack(answer.recordId, apiBaseUrl).then(packSaid)]} /></span>,
          /* 🔒 Delete Deal under More (decision 12700): its window reads what the Deal holds and refuses a Deal holding a document by name. */
          dealDelete: () => <span data-record-action="delete-deal"><Button size={Size.Small} color={Color.Inactive} text={[t('deal.action.delete')]} actions={[() => setDeleteOpen(true)]} /></span>,
          dealApprove: () => <Button size={Size.Small} color={Color.Navy} text={[t('deal.action.approve')]} actions={[() => void decideQuick('Approve')]} />,
          dealReject: () => <Button size={Size.Small} color={Color.Inactive} text={[t('deal.action.reject')]} actions={[() => void decideQuick('Reject')]} />,
          /* The gate notice stands only where the address names one, which is a reading of the address and not of the facts. */
          dealGate: () => (gate ? <span className={styles.bannerNumber} data-deal-gate={gate}>{t('deal.gate.opened')}</span> : null),
          /* The Lead's one-press actions (piece 20): each writes the status the action causes and nothing else. */
          leadCallAttempt: () => <Button size={Size.Small} color={Color.Inactive} text={[t('lead.action.callAttempt')]} actions={[() => void leadAct('call-attempt')]} />,
          leadConvertDeal: () => <Button size={Size.Small} color={Color.Navy} text={[t('lead.action.convertDeal')]} actions={[() => setLeadModal('convertDeal')]} />,
          leadConvertCompany: () => <Button size={Size.Small} text={[t('lead.action.convertCompany')]} actions={[() => void leadAct('convert-to-company')]} />,
          leadRelease: () => <Button size={Size.Small} color={Color.Navy} text={[t('lead.action.release')]} actions={[() => void leadAct('release')]} />,
          leadHot: () => <Button size={Size.Small} text={[t('lead.action.hot')]} actions={[() => void leadAct('temperature', { temperature: 'Hot' })]} />,
          leadNotFit: () => <Button size={Size.Small} text={[t('lead.action.notFit')]} actions={[() => setLeadModal('notFit')]} />,
          leadRedirect: () => <Button size={Size.Small} text={[t('lead.action.redirect')]} actions={[() => setLeadModal('redirect')]} />,
          leadDispatch: () => <Button size={Size.Small} text={[t('lead.action.dispatch')]} actions={[() => setLeadModal('dispatch')]} />,
          leadBlock: () => <Button size={Size.Small} text={[t('lead.action.block')]} actions={[() => void leadAct('block')]} />,
          /* 🔨 The suggested match sits beside the submitted text with Link to this and Create new; nothing resolves itself. */
          leadSuggestedMatch: () => (
            <div className={styles.actions} data-lead-suggested-match>
              {typeof facts.suggestedCompanyId === 'string' && !facts.companyId ? (
                <>
                  <span className={styles.bannerNumber}>{line(t('lead.match.company'), String(facts.suggestedCompanyName ?? ''))}</span>
                  <Button size={Size.Small} text={[t('lead.match.link')]} actions={[() => void leadAct('link', { companyId: facts.suggestedCompanyId })]} />
                  <Button size={Size.Small} text={[t('lead.match.createNew')]} actions={[() => void leadAct('convert-to-company')]} />
                </>
              ) : null}
              {typeof facts.suggestedPersonId === 'string' && !facts.personId ? (
                <>
                  <span className={styles.bannerNumber}>{line(t('lead.match.person'), String(facts.suggestedPersonName ?? ''))}</span>
                  <Button size={Size.Small} text={[t('lead.match.link')]} actions={[() => void leadAct('link', { personId: facts.suggestedPersonId })]} />
                </>
              ) : null}
            </div>
          ),
        };
        /** The actions this page's own answer offers, in the registry's order. */
        const offeredActions = pageSpec.headerActions.filter((action) => action.when === undefined || action.when(facts));
        const drawActions = (group: 'beforeTags' | 'row' | 'more' | 'below') =>
          offeredActions
            .filter((action) => (action.group ?? 'row') === group)
            .map((action) => ({ key: action.key, drawn: bannerActionFor[action.key]() }))
            // 🔒 T2985: an act whose draw answers nothing, such as the Deal gate with no gate, takes no place in the four.
            .filter((act) => act.drawn !== null && act.drawn !== undefined && act.drawn !== false)
            .map((act) => <Fragment key={act.key}>{act.drawn}</Fragment>);

        /**
         * 🔒 One map from a modal key to the window that draws it (T180; decision
         * 0631). The registry names a page's windows by key, because
         * `packages/shared` is DOM-free (decision 0242); this is the one place a
         * key becomes a component, and a key with no entry here refuses to
         * compile.
         */
        const modalFor: Readonly<Record<RecordModalKey, () => ReactNode>> = {
          /* 🔨 Keyed to the certificate it edits, so a second row opened after the first does not carry the first one's facts (decision 0676). */
          siteCertificate: () => <CertificateModal key={modal === 'addCertificate' ? `cert-open-${editingCertificate?.id ?? 'new'}` : 'cert-closed'} open={modal === 'addCertificate'} siteId={answer.recordId} editing={editingCertificate} apiBaseUrl={apiBaseUrl} onCancel={() => { setEditingCertificate(null); close(); }} onSaved={saved} />,
          /* 🔒 Decision 2851: the four steps with the Request a certificate panel under them, opened from the certificates tile's header and unmounted when closed. */
          siteExemptSteps: () => (
            <ExemptCustomerStepsWindow
              open={stepsOpen}
              onCancel={() => setStepsOpen(false)}
              siteId={answer.recordId}
              apiBaseUrl={apiBaseUrl}
              revision={answer}
              requestPanel={<CertificateRequestsPanel siteId={answer.recordId} apiBaseUrl={apiBaseUrl} zone={timeZone} dateFormat={dateFormat} editable={ownEditable} onChanged={reload} />}
              {...(ownEditable ? {
                onRecordCertificate: () => { setEditingCertificate(null); setModal('addCertificate'); },
                onEditCertificate: (certificateId: string) => {
                  const row = (answer.tiles.find((tile) => tile.key === 'certificates')?.rows ?? []).find((one) => one.id === certificateId);
                  if (row) editCertificateRow(row);
                },
                // 🔒 Decision 7891: the steps' certificate offers the card's own Move and Remove, where the card offers them.
                certificateActs: (certificateId: string) => {
                  const row = (answer.tiles.find((tile) => tile.key === 'certificates')?.rows ?? []).find((one) => one.id === certificateId);
                  return row ? certificateMenuActs(row, { removeCertificate: (one) => setRemovingCertificate({ id: one.id ?? '', label: one.label, value: one.value ?? '' }), moveCertificate: (one) => setMovingCertificate({ id: one.id ?? '', label: one.label, value: one.value ?? '' }) }) : [];
                },
              } : {})}
            />
          ),
          siteCertificateEnd: () => <CertificateEndModal key={endingCertificate ? `cert-end-${endingCertificate.id}` : 'cert-end-closed'} open={endingCertificate !== null} siteId={answer.recordId} row={endingCertificate} apiBaseUrl={apiBaseUrl} onCancel={() => setEndingCertificate(null)} onSaved={saved} />,
          siteCertificateRemove: () => <CertificateRemoveModal key={removingCertificate ? `cert-remove-${removingCertificate.id}` : 'cert-remove-closed'} open={removingCertificate !== null} siteId={answer.recordId} row={removingCertificate} apiBaseUrl={apiBaseUrl} onCancel={() => setRemovingCertificate(null)} onSaved={saved} />,
          siteCertificateMove: () => <CertificateMoveModal key={movingCertificate ? `cert-move-${movingCertificate.id}` : 'cert-move-closed'} open={movingCertificate !== null} siteId={answer.recordId} row={movingCertificate} apiBaseUrl={apiBaseUrl} onCancel={() => setMovingCertificate(null)} onSaved={saved} />,
          siteRequirement: () => <SiteRequirementModal key={modal === 'addRequirement' ? 'req-open' : 'req-closed'} open={modal === 'addRequirement'} siteId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={close} onSaved={saved} />,
          siteRole: () => <SiteRoleModal key={modal === 'addSiteRole' ? 'role-open' : 'role-closed'} open={modal === 'addSiteRole'} siteId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={close} onSaved={saved} />,
          /* 🔨 Keyed to the requirement it ends, so a second row opened after the first does not carry the first one's day. */
          siteRequirementEnd: () => <SiteRequirementEndModal key={endingRequirement ? `req-end-${endingRequirement.id}` : 'req-end-closed'} open={endingRequirement !== null} siteId={answer.recordId} row={endingRequirement} apiBaseUrl={apiBaseUrl} onCancel={() => setEndingRequirement(null)} onSaved={saved} />,
          /* The Item page's own windows (piece 17): an attribute, a supplier, a site the Item is stocked at, and its image. */
          itemAttribute: () => <ItemAttributeModal key={modal === 'addAttribute' ? `attr-${editingAttribute ?? 'new'}` : 'attr-closed'} open={modal === 'addAttribute'} attributeId={editingAttribute} itemId={answer.recordId} categoryId={typeof facts.categoryId === 'string' ? facts.categoryId : null} apiBaseUrl={apiBaseUrl} onCancel={close} onSaved={saved} />,
          itemSupplier: () => <ItemSupplierModal key={modal === 'addSupplier' ? `sup-open-${editingSupplier?.id ?? 'new'}` : 'sup-closed'} open={modal === 'addSupplier'} itemId={answer.recordId} initial={editingSupplier} apiBaseUrl={apiBaseUrl} onCancel={() => { setEditingSupplier(null); close(); }} onSaved={saved} />,
          itemSiteStock: () => <ItemSiteStockModal key={modal === 'stockAtSite' ? 'stock-open' : 'stock-closed'} open={modal === 'stockAtSite'} itemId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={close} onSaved={saved} />,
          /* 🔒 Decision 1142: unmounted when closed, so a second issue opens on empty fields. */
          itemIssueStock: () => (modal === 'issueStock' ? (
            <IssueStockModal
              item={{ id: answer.recordId, label: answer.title }}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onIssued={(issued) => { close(); setConfirmation(`${t('stock.issue.done')} ${issued.onHandAfter} of ${issued.sku} at ${issued.siteName}.`); saved(); }}
            />
          ) : null),
          itemImage: () => <ItemImageModal key={modal === 'setImage' ? 'image-open' : 'image-closed'} open={modal === 'setImage'} itemId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={close} onSaved={saved} />,
          /* The occupancy edit, on both tiles that draw a site row (decision 0589). */
          siteOccupancy: () => (
            <SiteOccupancyModal
              key={modal === 'editOccupancy' ? `occupancy-edit-${editingOccupancy?.siteId ?? 'none'}` : 'occupancy-edit-closed'}
              open={modal === 'editOccupancy'}
              editing={editingOccupancy}
              apiBaseUrl={apiBaseUrl}
              onCancel={() => { setEditingOccupancy(null); close(); }}
              onSaved={saved}
            />
          ),
          locationOccupant: () => (
            <SiteModal
              key={modal === 'addOccupant' ? 'occupant-open' : 'occupant-closed'}
              open={modal === 'addOccupant'}
              locationId={answer.recordId}
              addressLine={answer.title}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onCreated={saved}
            />
          ),
          /* 🔨 Keyed to the record it edits and to whether it is open, so reopening it on another record does not show the first one's pick (decision 0616).
             🔒 The picture is settable on a Person, a Company and a Location and on no other record type (T170; decision 0667). */
          recordPicture: () => (
            <RecordPictureModal
              key={modal === 'setPhoto' ? `picture-${answer.recordId}` : 'picture-closed'}
              open={modal === 'setPhoto'}
              kind={pageSpec.picture ?? 'person'}
              recordId={answer.recordId}
              pictureFileId={answer.bannerAvatar?.imageFileId ?? null}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onSaved={saved}
            />
          ),
          /* 🔨 The employment edit is mounted for the Company page as well as the
             Person page (decision 0589): the Contacts tile lists the same
             employments from the other end, and the row had no pencil at all while
             this stood inside the Person block. 🔨 Keyed to the employment it edits,
             so a second row opened after the first does not show the first one's
             draft (piece 13). */
          employmentEdit: () => (
            <>
            {/* 🔒 Set level (T726; decision 1495), unmounted when closed and keyed to the employment it sets, so a second row never shows the first one's draft. */}
            {settingLevel ? <SetLevelModal key={`set-level-${settingLevel.employmentId}`} editing={settingLevel} apiBaseUrl={apiBaseUrl} onCancel={() => setSettingLevel(null)} onSaved={() => { setSettingLevel(null); saved(); }} /> : null}
            <EmploymentModal
              key={modal === 'editEmployment' ? `employment-edit-${editingEmployment?.employmentId ?? 'none'}` : 'employment-edit-closed'}
              open={modal === 'editEmployment'}
              origin={pageSpec.employmentOrigin ?? 'person'}
              personId={pageSpec.employmentOrigin === 'person' ? answer.recordId : null}
              personName={pageSpec.employmentOrigin === 'person' ? answer.title : null}
              companyId={pageSpec.employmentOrigin === 'company' ? answer.recordId : null}
              companyName={pageSpec.employmentOrigin === 'company' ? answer.title : null}
              editing={editingEmployment}
              apiBaseUrl={apiBaseUrl}
              onCancel={() => { setEditingEmployment(null); close(); }}
              onCreated={saved}
            />
            </>
          ),
          /* A new employment from whichever end the page is: the company locked and the person
             searched or created, or the other way about (decision 0165). The two ends carry
             different header actions, so which one is open is read off the page's own end. */
          employmentAdd: () => {
            const fromCompany = pageSpec.employmentOrigin === 'company';
            const openFlag = modal === (fromCompany ? 'addEmployed' : 'addEmployment');
            return (
              <EmploymentModal
                key={openFlag ? 'employment-open' : 'employment-closed'}
                open={openFlag}
                origin={fromCompany ? 'company' : 'person'}
                personId={fromCompany ? null : answer.recordId}
                personName={fromCompany ? null : answer.title}
                companyId={fromCompany ? answer.recordId : null}
                companyName={fromCompany ? answer.title : null}
                apiBaseUrl={apiBaseUrl}
                onCancel={close}
                onCreated={saved}
              />
            );
          },
          /* Decisions 0464 and 0614: what this person is recorded as able to do — each skill whole, or a sub-skill beneath one. */
          personSkill: () => <SkillModal key={modal === 'addSkill' ? `skill-${answer.recordId}` : 'skill-closed'} open={modal === 'addSkill'} personId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={close} onSaved={saved} />,
          /* 🔒 Decision 6400: + Add School, the page's own window as the Employment window is,
             because a folded Education tile draws no panel. Mounted only while open; a save
             tells the Education panel to read again, whether or not it is drawn. */
          /* 🔒 T2352: + Grant Role, the page's own window, mounted only while open; a grant tells the tile to read again. */
          personConfidentialityGrant: () => (modal === 'grantConfidentialityRole' ? (
            <GrantRoleWindow
              key={`grant-role-${answer.recordId}`}
              personId={answer.recordId}
              dateFormat={dateFormat}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onSaved={() => { close(); announceConfidentiality(); }}
            />
          ) : null),
          personEducation: () => (modal === 'addEducation' ? (
            <EducationModal
              key={`education-add-${answer.recordId}`}
              personId={answer.recordId}
              initial={null}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onSaved={() => { close(); announcePersonHistory(); }}
            />
          ) : null),
          personContactMethod: () => (
            <ContactMethodModal
              key={modal === 'addContactMethod' ? `contact-open-${editingContact?.id ?? 'new'}` : 'contact-closed'}
              open={modal === 'addContactMethod'}
              employmentId={typeof facts.headlineEmploymentId === 'string' ? facts.headlineEmploymentId : null}
              jobs={contactMethodJobs(facts)}
              initial={editingContact}
              apiBaseUrl={apiBaseUrl}
              onCancel={() => { setEditingContact(null); close(); }}
              onSaved={saved}
            />
          ),
          /* Piece 9's NDA upload, from the Company page's Access & agreements tile. */
          companyNda: () => <NdaModal key={modal === 'addNda' ? 'nda-open' : 'nda-closed'} open={modal === 'addNda'} apiBaseUrl={apiBaseUrl} companyId={answer.recordId} companyName={answer.title} onCancel={close} onSaved={saved} />,
          /* 🔨 Keyed to the company it edits: a modal mounted-and-hidden reads its `initial` prop once, and a second account would open on the first one's draft. */
          /* 🔒 Decision 1172: the client's terms set, unmounted when shut so a second opening reads the sets again. */
          /* 🔒 Decision 2972: the Mailboxes tile's window, mounted only while open and keyed to the row it edits. */
          companyMailbox: () => (modal === 'addMailbox' ? (
            <CompanyMailboxModal
              key={`mailbox-${editingMailbox?.id ?? 'new'}`}
              companyId={answer.recordId}
              initial={editingMailbox}
              apiBaseUrl={apiBaseUrl}
              onCancel={() => { setEditingMailbox(null); close(); }}
              onSaved={saved}
            />
          ) : null),
          /* 🔒 Decision 7501: + Add Owner and End Ownership, keyed to the ownership it ends so a second never opens on the first one's day. */
          companyOwnership: () => (ownershipWindow ? (
            <CompanyOwnershipModal
              key={ownershipWindow.mode === 'end' ? `ownership-end-${ownershipWindow.ownership.id}` : 'ownership-add'}
              companyId={answer.recordId}
              companyName={answer.title}
              opening={ownershipWindow}
              apiBaseUrl={apiBaseUrl}
              onCancel={() => setOwnershipWindow(null)}
              onSaved={saved}
            />
          ) : null),
          companyTermsSet: () => (choosingTermsSet ? (
            <CompanyTermsSetModal
              companyId={answer.recordId}
              companyName={answer.title}
              setId={choosingTermsSet.setId}
              apiBaseUrl={apiBaseUrl}
              onCancel={() => setChoosingTermsSet(null)}
              onSaved={() => { setChoosingTermsSet(null); saved(); }}
            />
          ) : null),
          companyAccountStanding: () => (
            <AccountStandingModal
              key={modal === 'editAccountStanding' ? `credit-${answer.recordId}` : 'credit-closed'}
              open={modal === 'editAccountStanding'}
              companyId={answer.recordId}
              initial={accountStandingFrom(answer)}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onSaved={saved}
            />
          ),
          companySite: () => (
            <SiteModal
              key={modal === 'addSite' ? 'site-open' : 'site-closed'}
              open={modal === 'addSite'}
              companyId={answer.recordId}
              companyName={answer.title}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onCreated={saved}
            />
          ),
          companyAccountRole: () => <AccountRoleModal key={leadModal === 'accountRole' ? 'ar-open' : 'ar-closed'} open={leadModal === 'accountRole'} companyId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={() => setLeadModal(null)} onSaved={saved} />,
          companyVendorRep: () => <VendorRepModal key={modal === 'addVendorRep' ? 'vr-open' : 'vr-closed'} open={modal === 'addVendorRep'} vendorCompanyId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={close} onChanged={reload} />,
          /* The Deal page's own windows (piece 19). */
          dealLost: () => <LostDealModal key={lostOpen ? 'lost-open' : 'lost-closed'} open={lostOpen} dealId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={() => setLostOpen(false)} onSaved={saved} />,
          /* 🔨 Keyed to its open state, so a second open starts on the Deal's own Account again. */
          dealClone: () => <CloneDealModal key={cloneOpen ? `clone-open-${answer.recordId}` : 'clone-closed'} open={cloneOpen} dealId={answer.recordId} companyId={typeof facts.companyId === 'string' ? facts.companyId : null} companyName={typeof facts.companyName === 'string' ? facts.companyName : null} apiBaseUrl={apiBaseUrl} onCancel={() => setCloneOpen(false)} onCloned={(id) => { setCloneOpen(false); navigate(`/deals/${id}`); }} />,
          dealDelete: () => <DealDeleteModal key={deleteOpen ? `delete-open-${answer.recordId}` : 'delete-closed'} open={deleteOpen} dealId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={() => setDeleteOpen(false)} onDeleted={() => { setDeleteOpen(false); navigate(LIST_PAGES.Deal.route, { replace: true }); }} />,
          dealTeam: () => <DealTeamModal key={modal === 'addTeamMember' ? `team-open-${editingMember?.id ?? 'new'}` : 'team-closed'} open={modal === 'addTeamMember'} dealId={answer.recordId} companyId={companyId} initial={editingMember} apiBaseUrl={apiBaseUrl} onCancel={() => { setEditingMember(null); close(); }} onSaved={saved} />,
          /* 🔨 Keyed to its open state, so a second open starts with nobody picked. */
          dealOwner: () => <ChangeOwnerModal key={modal === 'changeOwner' ? 'owner-open' : 'owner-closed'} open={modal === 'changeOwner'} dealId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={close} onSaved={saved} />,
          dealContact: () => <DealContactModal key={modal === 'addDealContact' ? 'dc-open' : 'dc-closed'} open={modal === 'addDealContact'} dealId={answer.recordId} companyId={companyId} apiBaseUrl={apiBaseUrl} onCancel={close} onSaved={saved} />,
          /* 🔨 The same modal the bill of materials' footer opens, reached from the tile that lists
             what it produces (decision 0495). Keyed to the open flag, because a modal mounted and
             hidden reads its lines once and would show the first attempt's ticks on the second. */
          dealRfq: () => (
            <RfqModal
              key={modal === 'newRfq' ? 'rfq-open' : 'rfq-closed'}
              open={modal === 'newRfq'}
              dealId={answer.recordId}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onSent={(sent) => {
                close();
                saved();
                // The same sentence the editor's own button reports, so the outcome reads identically whichever way in was used.
                setNotice(sent.send === 'Accepted' || sent.send === 'Deferred' ? line(sent.rfqNumber, t('deal.rfq.sent'), sent.to) : line(sent.rfqNumber, t('deal.rfq.sendFailed')));
              }}
            />
          ),
          // 🔒 Decision 11480: the one Release window, mounted by the Deal, the Company and the Location, each listing its own companies.
          releaseFile: () => (
            <ReleaseFileModal
              key={release ? `release-${release.fileId}` : 'release-closed'}
              open={release !== null}
              fileId={release?.fileId ?? null}
              fileName={release?.fileName ?? null}
              companies={releaseTargets.companies}
              customerCompanyId={releaseTargets.customerCompanyId}
              {...(recordType === 'Deal' ? {} : { noTargets: t('files.release.noTargets') })}
              apiBaseUrl={apiBaseUrl}
              onCancel={() => setRelease(null)}
              onSaved={(message) => { setNotice(message); reload(); }}
            />
          ),
          /* The Lead page's own dialogs (piece 20): Not a Fit, Convert to Deal, Redirect and Dispatch. */
          leadNotFit: () => <NotAFitModal key={leadModal === 'notFit' ? 'nf-open' : 'nf-closed'} open={leadModal === 'notFit'} leadId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={() => setLeadModal(null)} onSaved={saved} />,
          leadConvertDeal: () => <ConvertToDealModal key={leadModal === 'convertDeal' ? 'cd-open' : 'cd-closed'} open={leadModal === 'convertDeal'} leadId={answer.recordId} overrideOffered={facts.overrideOffered === true} negotiationComplete={facts.negotiationComplete === true} apiBaseUrl={apiBaseUrl} onCancel={() => setLeadModal(null)} onSaved={saved} />,
          leadRedirect: () => <RedirectModal key={leadModal === 'redirect' ? 'rd-open' : 'rd-closed'} open={leadModal === 'redirect'} leadId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={() => setLeadModal(null)} onSaved={saved} />,
          leadDispatch: () => <DispatchModal key={leadModal === 'dispatch' ? 'dp-open' : 'dp-closed'} open={leadModal === 'dispatch'} leadId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={() => setLeadModal(null)} onSaved={saved} />,
        };

        /**
         * The page above its tiles: a refusal it has to report, the banner's own
         * action row, and any action the record type draws as a row beneath it.
         * 🔒 On a document page it stands above both views, where it has always
         * stood (T181).
         */
        /* 🔒 The acts under the banner stand on one row, four at most, and the rest sit under More (decision 6685, piece 13). Log Contact counts as the first. */
        const actRoom = (pageSpec.logsContact && writes ? MAX_BANNER_ACTS - 1 : MAX_BANNER_ACTS);
        const beforeAll = drawActions('beforeTags');
        const rowAll = drawActions('row');
        const beforeActs = beforeAll.slice(0, actRoom);
        const rowActs = rowAll.slice(0, Math.max(0, actRoom - beforeActs.length));
        const overflowActs = [...beforeAll.slice(beforeActs.length), ...rowAll.slice(rowActs.length)];
        const header = (
          <>
            {notice && (
              <div className={styles.refusal}>
                <AlertBanner tone="error" message={notice} action={{ label: t('modal.dismiss'), onAction: () => setNotice(null) }} />
              </div>
            )}
            {confirmation && (
              <div className={styles.refusal} data-record-confirmation="true">
                <AlertBanner tone="good" message={confirmation} action={{ label: t('modal.dismiss'), onAction: () => setConfirmation(null) }} />
              </div>
            )}
            <div className={styles.actions}>
              {/* + Log Contact is a header action, not a tile (decision 0185); it opens the same Note modal, pre-set to a call or a meeting. 🔨 A party's page only: an Item and a Location are things (decision 0280), which the registry says (T180). */}
              {pageSpec.logsContact && writes ? <Button size={Size.Small} text={[t('record.logContact')]} actions={[() => setModal('logContact')]} /> : null}
              {/* 🔒 Decision 5130: the office number's call stands beside + Log Contact on a Person and a Company, where the viewer is offered it, because the website's Call is a link inside the contact rows. */}
              {pageSpec.logsContact && writes && (answer.recordType === 'PersonRecord' || answer.recordType === 'CompanyRecord') ? <OfficeCallButton recordType={answer.recordType} recordId={answer.recordId} apiBaseUrl={apiBaseUrl} onWritten={reload} onError={setNotice} /> : null}
              {/* 🔒 Decision 5382: the Check act in the title band, for a writer of the record's module; it draws itself shut with its reason. */}
              {writes && (answer.recordType === 'PersonRecord' || answer.recordType === 'CompanyRecord') ? <RecordCheckAct key={`check-${answer.recordId}`} recordType={answer.recordType} recordId={answer.recordId} apiBaseUrl={apiBaseUrl} onError={setNotice} /> : null}
              {/* 🔒 An action the record type's own entry puts ahead of the tag chips (T180). */}
              {beforeActs}
              {/* 🔨 No + New Purchase Order here (T79, on decision 0497): work enters at the Deal, and
                  an order raised straight off a vendor's page started with no job above it saying what
                  it was for. A purchase order is raised from the Purchase Orders list or from a Deal.
                  The Work tile below still lists what has been sent to this vendor. */}
              {/* The tags sit on this row beside Log Contact, each chip with its own remove, and + Add Tag stands at the row's right (product owner, 2026-09-11). */}
              {/* 💰 The chip drew in status-inactive and the product owner reported that it blends (2026-09-14). Its tint reads 1.13:1 on white, so the fill never said where the chip ended; TagChip outlines it in the swatch's own text hex, at 4.8:1 or better on every ground a chip sits on. */}
              {/* 🔒 Eight tags or fewer draw in this row, grouped by category in the Settings order; past eight the grouped strip draws beneath (decision 2260). */}
              {bannerTags.length > 0 && !drawsGroupedStrip(bannerTags.length) ? <RecordTagPills tags={bannerTags} {...tagControls} /> : null}
              {contactsTile && isOwnCompany ? <span className={styles.bannerNumber}>{t('record.hireHint')}</span> : null}
              {/* 🔨 No + New Work Order here either (T79, on decision 0497): a job is raised from the Deal
                  it belongs to or from the Work Orders list, never from an account or a site with nothing
                  above it. The Work tile on both pages lists the jobs already raised. */}
              {/* 🔒 The actions the record type's own entry names, in its own order (T180). */}
              {rowActs}
              {/* 🔨 The rest sit behind the overflow the type declares, so the banner is not nine identical buttons (decision 0277). */}
              {pageSpec.more || overflowActs.length > 0 || superAdmin ? (
                <details className={styles.more} onToggle={(event) => setMoreOpen(event.currentTarget.open)} {...(pageSpec.more ? { [pageSpec.more.attribute]: 'true' } : { 'data-record-more': 'true' })}>
                  <summary className={styles.moreSummary}>{t(pageSpec.more ? pageSpec.more.labelKey : 'record.action.more')}</summary>
                  <div className={styles.moreList}>
                    {overflowActs}
                    {drawActions('more')}
                    {/* 🔒 Decision 7620: Inspect stands last in the menu, for a super admin alone. */}
                    {superAdmin && moreOpen ? <span data-record-inspect="true"><Button size={Size.Small} text={[t('record.inspect')]} actions={[() => setInspecting(true)]} /></span> : null}
                  </div>
                </details>
              ) : null}
              {bannerTile && tagsWrite ? <span className={styles.pushRight}><Button size={Size.Small} text={[TILE_ACTION_LABELS.addTag]} actions={[() => setModal('addTag')]} /></span> : null}
            </div>
            {drawsGroupedStrip(bannerTags.length) ? <RecordTagStrip tags={bannerTags} {...tagControls} /> : null}
            {/* 🔒 Each note on this record's tags, in full, beneath its tags (decision 2261). */}
            <RecordTagNotes tags={bannerTags} />
            {/* 🔒 An action that is a row of its own beneath the banner's, where the type names one (T180). */}
            {drawActions('below')}
          </>
        );
        /** The record's own tiles: the staged layout where the type has one, the canvas otherwise. */
        const flow =           staged ? (

            // A staged record is one custom layout, not the canvas (decision 0293; figures 81 and 82).
            <StagedLayout
              before={flowOf(tiles, (key) => staged.before.includes(key))}
              stagesKey={staged.stagesKey}
              stages={panelFor[staged.stages]()}
              notice={staged.notice ? panelFor[staged.notice]() : null}
              phoneCurrentFirst={staged.phoneCurrentFirst === true}
              after={flowOf(tiles, (key) => !staged.elsewhere.includes(key) && !staged.before.includes(key))}
              /* 🔒 The bands the type names, each band's tiles in its own order and any the answer lacks left out (decision 1215). */
              bands={staged.bands ? staged.bands.map((band, at) => (band === 'stages' ? { kind: 'stages' as const } : { kind: 'tiles' as const, key: band.join('-') || String(at), tiles: band.flatMap((key) => flowOf(tiles, (id) => id === key)) })) : null}
              panelTitle={t(staged.panelTitleKey)}
              panel={panelFor[staged.panel]()}
              rail={panelFor[staged.rail]()}
              storageKey={`${PROGRESS_PANEL_KEY_PREFIX}${recordType}`}
            />
          ) : (
            <TileCanvas tiles={tiles} options={RECORD_CANVAS_OPTIONS} label={answer.title} />
          );
        const modals = (
          <>
            {/* 🔒 Decision 7620: mounted only while open, so each opening reads the counts afresh. */}
            {inspecting && superAdmin ? <InspectWindow recordType={recordType} recordId={answer.recordId} title={answer.title} apiBaseUrl={apiBaseUrl} onClose={() => setInspecting(false)} /> : null}
            {/* 🔒 Decision 5382: an employer suggestion opens the employment window, an address + Add Site, each prefilled; nothing is written until its Save. */}
            {suggestionWindow && suggestionWindow.row.field === 'Employer' && answer.recordType === 'PersonRecord' ? (
              <EmploymentModal
                key={`suggested-employer-${suggestionWindow.row.id}`}
                open
                origin="person"
                personId={answer.recordId}
                personName={answer.title}
                companyHint={suggestionWindow.row.suggested}
                apiBaseUrl={apiBaseUrl}
                onCancel={() => setSuggestionWindow(null)}
                onCreated={() => { const { stamp } = suggestionWindow; setSuggestionWindow(null); void stamp().then(saved); }}
              />
            ) : null}
            {suggestionWindow && suggestionWindow.row.field === 'Address' && answer.recordType === 'CompanyRecord' ? (
              <SiteModal
                key={`suggested-address-${suggestionWindow.row.id}`}
                open
                companyId={answer.recordId}
                companyName={answer.title}
                addressHint={suggestionWindow.row.suggested}
                addressDetail={suggestionWindow.row.detail}
                apiBaseUrl={apiBaseUrl}
                onCancel={() => setSuggestionWindow(null)}
                onCreated={() => { const { stamp } = suggestionWindow; setSuggestionWindow(null); void stamp().then(saved); }}
              />
            ) : null}
            {/* 🔒 Decision 0971: Accept opens the company's own edit window on the request, and saving it marks the request Accepted.
                Keyed to the request, and unmounted when closed, so a second Accept never opens on the first one's words. */}
            {accepting ? (
              <EditRecordModal
                key={`accept-${accepting.id}`}
                open
                recordType={recordType}
                recordId={answer.recordId}
                initial={editableFrom(answer)}
                apiBaseUrl={apiBaseUrl}
                request={acceptRequestOf(accepting, facts.editsRecord !== false)}
                onCancel={() => setAccepting(null)}
                onSaved={() => {
                  // 🔒 The window saved through the request's own Accept, which marked it Accepted in the same press (T355).
                  setAccepting(null);
                  setConfirmation(t('changeRequest.accepted'));
                  reload();
                }}
              />
            ) : null}
            <EditRecordModal
              key={modal === 'editRecord' ? `${answer.recordId}-${placedDraft?.count ?? 0}` : 'closed'}
              open={modal === 'editRecord'}
              recordType={recordType}
              recordId={answer.recordId}
              initial={editableFrom(answer)}
              apiBaseUrl={apiBaseUrl}
              prefill={placedDraft?.fields ?? null}
              verifyOnSave={placedDraft !== null}
              onCancel={() => { setPlacedDraft(null); close(); }}
              onSaved={() => { setPlacedDraft(null); saved(); }}
              onDeleted={() => navigate(spec.route)}
            />
            {/* 🔨 + Log Contact and a note's pencil open the full form; a note is added from the tile's composer (decision 0910).
                Keyed to the note it edits and unmounted when closed, so a second note never opens on the first one's draft. */}
            {modal === 'logContact' || modal === 'editNote' ? (
              <RecordNoteModal
                key={modal === 'logContact' ? 'note-logContact' : `note-edit-${editingNote?.id ?? ''}`}
                open
                recordType={recordType}
                recordId={answer.recordId}
                recordLabel={answer.title}
                apiBaseUrl={apiBaseUrl}
                noteId={modal === 'editNote' ? editingNote?.id ?? null : null}
                initial={modal === 'editNote' ? editingNote?.draft : undefined}
                onCancel={() => { setEditingNote(null); close(); }}
                onSaved={saved}
                onError={setNotice}
              />
            ) : null}
            {/* The colours reload beside the page: a tag added with a swatch draws in it at once rather than on the next visit. */}
            <TagModal open={modal === 'addTag'} recordType={recordType} recordId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={close} onSaved={() => { saved(); tagColours.reload(); }} />
            {noteTag ? <TagNoteModal key={noteTag.id} tag={noteTag} recordType={recordType} recordId={answer.recordId} apiBaseUrl={apiBaseUrl} onCancel={() => setNoteTag(null)} onSaved={() => { setNoteTag(null); tagColours.reload(); }} /> : null}
            <CustomFieldsModal
              key={modal === 'editCustomFields' ? 'cf-open' : 'cf-closed'}
              open={modal === 'editCustomFields'}
              recordType={recordType}
              recordId={answer.recordId}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onSaved={saved}
            />
            <FileDetailsModal key={modal === 'fileDetails' ? `file-${fileDetails?.id ?? ''}` : 'file-closed'} open={modal === 'fileDetails'} file={fileDetails} apiBaseUrl={apiBaseUrl} onCancel={close} onSaved={saved} />
            {/* 🔒 Delete this link (T728; decision 1500), mounted on every record page rather than in one record type's slot, because the Site's Roles,
                the Company's Owned By lines (decision 7501) and the Person's Custody rows carry the glyph too. Unmounted when closed and keyed to the link it deletes. */}
            {foldingSite ? <FoldSiteModal key={`fold-site-${foldingSite.fromId}`} fold={foldingSite} apiBaseUrl={apiBaseUrl} onCancel={() => setFoldingSite(null)} onFolded={(sentence) => { setFoldingSite(null); setConfirmation(sentence); saved(); }} /> : null}
            {deletingLink ? <DeleteLinkModal key={`delete-link-${deletingLink.kind}-${deletingLink.id}`} link={deletingLink} apiBaseUrl={apiBaseUrl} onCancel={() => setDeletingLink(null)} onDeleted={() => { setDeletingLink(null); saved(); }} /> : null}
            {/* 🔒 The windows this record type's own entry names, in its own order (T180). */}
            {pageSpec.modals.map((key) => <Fragment key={key}>{modalFor[key]()}</Fragment>)}
            <RecordUploadModal
              key={modal === 'upload' ? `upload-open-${uploadAct ?? 'files'}` : 'upload-closed'}
              open={modal === 'upload'}
              recordType={recordType}
              recordId={answer.recordId}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onSaved={saved}
              initialFiles={dropped}
              onScanConfirm={(file) => { close(); setScanConfirm(file); }}
              {...(recordType === 'Deal' ? { photoKinds: answer.equipmentPhotoKinds ?? [] } : {})}
              {...(uploadAct ? { act: uploadAct } : {})}
            />
            {/* Share on a file row (decision 0345): keyed to the file it shares, so a second row never shows the first's draft. */}
            {share ? (
              <ShareFileModal
                key={`share-${share.fileId}`}
                open
                file={{ id: share.fileId, filename: share.fileName, confidential: shareDetail.state.kind === 'ready' ? shareDetail.state.data.file.confidentialityRoleId !== null : false }}
                searchPeople={searchSharePeople}
                onDeliver={async (route: ShareRoute, draft: ShareDraft) => {
                  const delivered = await post<{ url: string; qrDataUrl?: string }>(`${FILES_PATH}/${share.fileId}/share`, { route, permission: draft.permission, recipientPersonId: draft.recipientPersonId, expiresAt: draft.expiresAt, email: draft.email }, apiBaseUrl);
                  if (!delivered.ok) {
                    setNotice(delivered.message);
                    throw new Error(delivered.message);
                  }
                  return delivered.data;
                }}
                onCancel={() => setShare(null)}
              />
            ) : null}
            <ScanConfirmModal
              key={scanConfirm ? `scan-${scanConfirm.id}` : 'scan-closed'}
              open={scanConfirm !== null}
              fileId={scanConfirm?.id ?? null}
              filename={scanConfirm?.filename ?? null}
              apiBaseUrl={apiBaseUrl}
              onCancel={() => { setScanConfirm(null); reload(); }}
              onComplete={(message) => { setScanConfirm(null); setNotice(message); reload(); }}
            />
            <RecordEventModal
              open={modal === 'addEvent'}
              recordType={recordType}
              recordId={answer.recordId}
              ownerPersonId={personId}
              timeZone={timeZone}
              apiBaseUrl={apiBaseUrl}
              onCancel={close}
              onSaved={saved}
              onError={setNotice}
              editing={editingEvent}
              associatedPerson={associatedPersonFor(pageSpec, answer.recordId, answer.title, facts)}
            />
          </>
        );
        /** 🔒 The banner's status pills and the record's own raised flags, on either frame. */
        const statusNode = pills.length > 0 || flagRows.length > 0 ? (
          <BannerPills pills={pills} attribute="data-record-pills">
            {/* 🔒 Decision 7690: who bought a Bought Out company, and whom this one bought, stands beside the status pill. */}
            <SuccessionSentence succession={answer.succession} />
            {flagRows.filter((row) => row.value === 'flag').map((row) => (
              <span key={`flag-${row.id ?? row.label}`} className={styles.flagPill} data-record-flag={row.label}>
                <Pill text={row.label} foreground="var(--status-warning-text)" background="var(--status-warning-bg)" size={Size.Medium} />
                {row.id ? <button type="button" className={styles.clearFlag} aria-label={t('record.clearFlag')} onClick={() => void clearFlag(row.id!)} data-record-clear-flag={row.id}><Icon meaning="remove" size="inline" /></button> : null}
              </span>
            ))}
          </BannerPills>
        ) : null;
        /**
         * 🔒 One read the whole page shares, where the record type's entry names one
         * (T180). 🔨 The Work Order's options are read by its lines panel and by its
         * footer: each held its own and the page asked twice (found in review,
         * 2026-09-15).
         */
        const shared = (node: ReactNode) =>
          pageSpec.sharedRead === 'workOrderOptions'
            ? <WorkOrderOptions companyId={answer.workOrder?.companyId ?? null} apiBaseUrl={apiBaseUrl}>{node}</WorkOrderOptions>
            : node;

        /**
         * 🔒 The two document views, drawn by the one document view page (T181, on
         * decision 0612): the sheet with its corner ribbon, or the record's own tiles
         * behind Details, with the action bar on the floor and Edit at its right.
         */
        if (documentPage) {
          /* 🔒 The record's own Internal Notes tile is the PDF view's rail (T587; decision 1403); only one view is mounted, so it is drawn once. */
          const notesTile = tiles.find((tile) => tile.placement.id === 'internalNotes');
          /* 🔒 The job's Files block is the rail's Files tab, between Notes and History, read through the one document files read (T2984; decision 6800, piece 29). */
          /* 🔒 The record's History tile is the rail's History tab, beside Notes (T2977; decision 6683, piece 29), so the rail draws one band and one chevron like every other document's. */
          const historyRows = answer.tiles.find((tile) => tile.key === 'history')?.rows ?? null;
          return shared(
            <DocumentViewPage
              documentType={documentPage.documentType}
              icon={RECORD_ICONS[recordType] ?? 'file'}
              title={answer.title}
              subtitle={kindLine.kind === 'pills' ? null : kindLine.text}
              information={information}
              {...(statusNode === null ? {} : { status: statusNode })}
              gear={gear}
              footer={panelFor[documentPage.footer]()}
              footerAttributes={{ [documentPage.footerAttribute]: String(facts[documentPage.ribbonFact] ?? '') }}
              {...(facts.editable === true ? { onEdit: () => navigate(`${spec.route}/${answer.recordId}?mode=edit`) } : {})}
              pageAttributes={{ 'data-record-page': recordType, 'data-record-id': answer.recordId }}
              notices={header}
              sheet={answer.printSheet ?? null}
              layout={answer.printLayout ?? null}
              ribbon={typeof facts[documentPage.ribbonFact] === 'string' ? (facts[documentPage.ribbonFact] as string) : null}
              pdfPath={documentPdfPath(documentPage.pdfBase, answer.recordId)}
              listRoute={spec.route}
              apiBaseUrl={apiBaseUrl}
              terms={{ documentId: answer.recordId, onSaved: reload }}
              writeRefusal={answer.writeRefusal ?? null}
              details={flow}
              {...(notesTile ? { notes: notesTile.render() } : {})}
              {...(notesTile && isDocumentAttachmentKind(documentPage.documentType) ? { files: <DocumentFilesSection recordType={documentPage.documentType} recordId={answer.recordId} writeRefusal={answer.writeRefusal ?? null} apiBaseUrl={apiBaseUrl} /> } : {})}
              {...(notesTile && historyRows !== null ? { history: <DocumentHistoryPanel recordType={recordType} recordId={answer.recordId} rows={historyRows} timeZone={timeZone} foldable={false} /> } : {})}
            >
              {modals}
            </DocumentViewPage>,
          );
        }

        return shared(
          /* 🔒 The title bar standard (T170): the picture slot, the name, the kind
             line, the status pills and the Edit icon at the far right. No facts row,
             no caption and no contact column — the record's facts read on its tiles —
             and the one summary column the type's registry entry names (decision 0665). */
          <PageModule
            subtitle={kindNode}
            title={answer.title}
            /* 🔒 Decision 1472: on a phone the title bar folds to its first line whenever it stacks, folded by default. */
            collapsible
            avatar={(
              <BannerAvatarSlot
                {...(answer.bannerAvatar ? { avatar: answer.bannerAvatar } : {})}
                glyph={RECORD_ICONS[recordType] ?? 'file'}
                title={answer.title}
                apiBaseUrl={apiBaseUrl}
                onSetPhoto={pageSpec.picture === null ? null : () => setModal('setPhoto')}
              />
            )}
            {...(editable && ownEditable && writes && edits ? { onEdit: () => setModal('editRecord') } : {})}
            {...(statusNode === null ? {} : { status: statusNode })}
            /* 🔒 The summary column, where the record type's registry entry names one (decision 0665); the Deal is the one that does. */
            {...(pageSpec.bannerColumn ? { column: panelFor[pageSpec.bannerColumn]() } : {})}
            gear={gear}
          >
            <div data-record-page={recordType} data-record-id={answer.recordId}>
              {header}
              {flow}
              {modals}
            </div>
          </PageModule>,
        );
      }}
    </AsyncStatePanel>
  );
}

/** The route element: the id comes from the path, the record type from the route it is mounted on, and the viewer from the frame. */
export function RecordPageRoute({ recordType, apiBaseUrl = '' }: { recordType: PagedRecord; apiBaseUrl?: string }) {
  const { id = '' } = useParams();
  const shell = useOutletContext<ShellOutletContext | undefined>();
  return <RecordPage recordType={recordType} recordId={id} apiBaseUrl={apiBaseUrl} personId={shell?.personId ?? ''} employmentId={shell?.employmentId ?? null} timeZone={shell?.timeZone ?? 'UTC'} />;
}

/**
 * What the edit form opens on: the General Information rows the page already
 * read, matched to the editable fields by their label. 💰 The page reads rows
 * for display, so the form starts from what is on screen rather than a second
 * fetch that could disagree with it.
 */
function editableFrom(answer: PageAnswer): Record<string, unknown> {
  if (answer.editable) return answer.editable;
  const general = [...(answer.tiles.find((tile) => tile.key === 'generalInformation')?.rows ?? []), ...(answer.tiles.find((tile) => tile.key === 'billing')?.rows ?? [])];
  const byLabel = new Map(general.map((row) => [row.label, row.value ?? null]));
  const out: Record<string, unknown> = {};
  for (const field of EDIT_FIELDS[answer.recordType]) {
    const value = byLabel.get(field.label);
    if (value === undefined) continue;
    out[field.key] = field.kind === 'boolean' ? value === 'Yes' : field.kind === 'lines' ? String(value ?? '').split(', ') : value;
  }
  /* The page's own title, where the record type's entry says the form writes it as a field. */
  const titleKey = RECORD_PAGE_SPECS[answer.recordType].editTitleKey;
  if (titleKey !== null) out[titleKey] = answer.title;
  return out;
}

/**
 * The Account standing modal's opening values, off the same read the tile drew
 * (pieces 10 and 13). 🔨 The fixture a test hands the page can be missing any
 * of them, so each falls back rather than crashing the canvas.
 */
function accountStandingFrom(answer: PageAnswer): AccountStandingEdit {
  const editable = answer.editable ?? {};
  const status = editable.creditStatus;
  return {
    paymentTerms: typeof editable.paymentTerms === 'string' ? editable.paymentTerms : '',
    creditLimit: typeof editable.creditLimit === 'number' ? editable.creditLimit : null,
    creditStatus: (CREDIT_STATUSES as readonly string[]).includes(String(status)) ? (status as AccountStandingEdit['creditStatus']) : 'Good',
    currencyCode: typeof editable.currencyCode === 'string' ? editable.currencyCode : 'USD',
    defaultCreditLimit: typeof editable.defaultCreditLimit === 'number' ? editable.defaultCreditLimit : null,
  };
}

/**
 * The open jobs + Add contact method picks between, as the page answers them
 * (decision 0593). 🔨 A page that answers none leaves the picker out and the
 * window falls back to the headline employment, which is what it always used.
 */
function contactMethodJobs(facts: PageAnswer['facts']): ContactMethodJob[] {
  const raw = facts.openEmployments;
  if (typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw) as ContactMethodJob[];
    return Array.isArray(parsed) ? parsed.filter((row) => typeof row.id === 'string' && typeof row.label === 'string') : [];
  } catch (error) {
    reportCaughtInBrowser('records.contactMethodJobs.parse', error);
    return [];
  }
}

/** The companies a Deal's file can be offered to, as the page answers them: the Account and the contacts' companies (scenarios A21, C3). */
/** 🔒 The Deal's Next Action list as the banner and Deal Progress both read it (decisions 1219, 1485): the derived rows the answer carries, the first being the first action. */
/**
 * 🔒 Open Deal Progress (decision 1935): the ladder brought into view, where the next action is answered.
 * 🔨 Inside the staged layout the banner calls the layout's own opener instead (T1750); this is the fallback
 * where no layout stands, and it scrolls to the stage cards, since the progress panel is no tile of its own.
 */
function openDealProgress(): void {
  const ladder = document.querySelector('[data-stage-cards]');
  ladder?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
}

function nextActionRows(answer: PageAnswer): NextActionBannerRow[] {
  return (answer.tiles.find((tile) => tile.key === 'nextAction')?.rows ?? []).map((row) => ({ id: row.id ?? null, label: row.label, dueDate: typeof row.raw?.dueDate === 'string' ? row.raw.dueDate : null }));
}

